import React, { useState, useEffect } from 'react';
import { InlineNotificationPrompt } from './PushNotificationHandler';
import {
  Bell, ChevronRight, ChevronDown, ChevronLeft, Home, FileText,
  Phone, Building2, CreditCard, MapPin, TrendingUp,
  ArrowRight, Play, ExternalLink, X, Send, CheckCircle,
  MessageCircle, MessagesSquare, Calendar, Lightbulb, BellRing, Plane, Menu as MenuIcon,
  Video, Clock, Link, Mail, Flame, Timer, BedDouble, Ruler,
  Building, Store, Star, FolderOpen, Check
} from 'lucide-react';
import { useLanguage } from './LanguageContext';
import { isFilmMode } from '../portals/demo/demoMode';
import { DEMO_NOTIFICATIONS, DEMO_NEWS, DEMO_PLATFORM_LISTINGS, DEMO_UPCOMING_PROJECTS } from '../portals/demo/demoNotifications';
// Service catalogue lives in data/servicesCatalog so the pre-login
// "What we offer" showcase renders the same list these cards do.
import { SERVICES, ServiceItem } from '../data/servicesCatalog';
import { openWhatsApp, openCalendly, WHATSAPP_VISA_NUMBER } from '../portals/shared/contact';
import PayoutSummary from './PayoutSummary';
import { portfolioValueSeries, dldValueGrowth } from './portfolioMetrics';
import { cachedMarketIntelligence, storedMarketIntelligence, fetchMarketIntelligence } from './PortfolioView';
import BrainLoader from './BrainLoader';
import LiveTicker from './LiveTicker';
import CopilotBriefing from './CopilotBriefing';
import { groupTodos, saveTodoDone, fetchPaymentTodos } from './todos';
import { appLocale, appLanguage, trData } from '../i18n/locale';
// ✅ NEW: Import custom chat widget (replaces old RespondIOChat)
// ChatWidget moved to InvestorPortal.tsx

interface Notification {
  id: string;
  type: 'payment' | 'document' | 'construction' | 'announcement' | 'reminder' | 'webinar' | 'journey' | 'event';
  title: string;
  titleDe?: string;
  message: string;
  messageDe?: string;
  timestamp: Date;
  read: boolean;
  /** ticked off the Home to-do list — saved on the server, so it stays gone */
  done?: boolean;
  priority?: 'low' | 'medium' | 'high';
  actionUrl?: string;
  actionType?: 'internal' | 'external';
}

interface Unit {
  id: string;
  name?: string;
  unitNumber: string;
  project: string;
  developer?: string;
  status?: string;
  location?: string;
  currentValue?: number;
  image?: string;
  projectImage?: string;
  purchaseDate?: string;
  purchaseValue?: number;
  area?: string;
  type?: string;
  bedrooms?: number;
  constructionCompletionPercent?: number | null;
  constructionStatus?: string | null;
}

interface Investor {
  id: string;
  name: string;
  email?: string;
  advisor?: string | null;
  investorAdvisor?: string | null;
  investmentAdvisor?: string | null;
  /** Sample account (showcase / film mode) — the bell serves fixtures then. */
  isDemo?: boolean;
}

interface UpcomingProject {
  id: string;
  name: string;
  developer: string;
  location: string;
  images?: string[];
  video?: string;
  brochureUrl?: string;
  launchDate?: string;
}

/**
 * The portfolio value line, inside the portfolio value card.
 *
 * WHAT IT IS ALLOWED TO DRAW. There is no value history behind this app — the
 * Dubai Land Department data returns a valuation for today, not a series — so
 * the line is built from the only dated facts on record: each purchase, on the
 * day it happened, at the price paid. It steps up as the portfolio was
 * assembled and closes at today's market value. See portfolioValueSeries.
 *
 * It renders nothing at all when there are fewer than two points. A single
 * dot, or a flat line drawn through one purchase, says less than the figure
 * above it already does.
 */
const PortfolioValueTrend: React.FC<{ units: Unit[]; totalValue: number; onOpen: () => void }> = ({ units, totalValue, onOpen }) => {
  const { t, language } = useLanguage();
  const { points } = React.useMemo(
    () => portfolioValueSeries(units as any, totalValue, language === 'de' ? 'de-DE' : 'en-GB'),
    [units, totalValue, language],
  );

  if (points.length < 2) return null;

  const W = 300;
  const H = 76;
  const PAD = 8;
  const values = points.map((p) => p.value);
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  // A portfolio assembled in one year is nearly flat against its own range;
  // anchoring the floor at zero would flatten it to a straight line, so the
  // band is the data's own, with a guard for the degenerate case.
  const span = hi - lo || hi || 1;

  const xy = points.map((p, i) => ({
    ...p,
    x: (i / (points.length - 1)) * W,
    y: H - PAD - ((p.value - lo) / span) * (H - PAD * 2),
  }));

  const line = xy.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const area = `M0,${H} L${xy.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' L')} L${W},${H} Z`;
  const last = xy[xy.length - 1];

  return (
    <>
      <div className="pv-plot">
        <svg className="pv-chart" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true">
          <defs>
            <linearGradient id="pvFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#D6B160" stopOpacity="0.40" />
              <stop offset="100%" stopColor="#D6B160" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={area} fill="url(#pvFill)" />
          <polyline
            points={line}
            fill="none"
            stroke="#D6B160"
            strokeWidth="2"
            strokeLinejoin="round"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
        {/* Outside the stretched viewBox, so it stays a circle. */}
        <span className="pv-dot" style={{ top: `${(last.y / H) * 100}%` }} aria-hidden="true" />
      </div>

      <div className="pv-ticks">
        <span>{xy[0].label}</span>
        <span>{t('ipValueToday')}</span>
      </div>

      <button className="pv-cta" onClick={onOpen}>
        {t('ipViewPortfolio')} <ChevronRight size={14} />
      </button>
    </>
  );
};

interface MainDashboardProps {
  currentInvestor: Investor | null;
  units: Unit[];
  apiUrl: string;
  setActiveTab: (tab: string) => void;
  totalPortfolioValue?: number;
  onOpenChat?: () => void;
  /** Unread replies from the management team, for the header badge. */
  unreadMessages?: number;
  /**
   * Showcase / film: Property Management fixtures, because PM traffic is
   * blocked there and the revenue trend would otherwise read as a portfolio
   * with no tenants. See DEMO_PM_UNITS.
   */
  pmUnitsOverride?: any[];
  /** Showcase / film: the /dld/market-intelligence fixture, same reason. */
  dldOverride?: any;
  /** Opens the InvestMenter-KI (AI Wealth Co-Pilot) conversation with a message. */
  onAskCopilot?: (message: string) => void;
  /** Opens "Mein Gesamtvermögen". */
  onOpenWealth?: () => void;
}

// Fallback project images — admin can override these via the Property Photos tab in admin panel
const PROJECT_IMAGES: { [key: string]: string } = {
  'seagate':                     'https://i.imgur.com/Wq09VFd.jpeg',
  'sobha one':                   'https://i.imgur.com/cUQEinr.jpeg',
  'sobha hartland - the crest':  'https://i.imgur.com/1cg1mrf.jpeg',
  'the crest':                   'https://i.imgur.com/1cg1mrf.jpeg',
  'crest grande':                'https://i.imgur.com/En06J0C.jpeg',
  'silena':                      'https://i.imgur.com/MA8aZv1.jpeg',
  'silena residences':           'https://i.imgur.com/MA8aZv1.jpeg',
  'damac lagoons portofino':     'https://i.imgur.com/xPIGTnD.jpeg',
  'damac lagoons':               'https://i.imgur.com/xPIGTnD.jpeg',
  'portofino':                   'https://i.imgur.com/xPIGTnD.jpeg',
  'potofino':                    'https://i.imgur.com/xPIGTnD.jpeg',
  'gp chevalia fields':          'https://i.imgur.com/piVz6YW.jpeg',
  'chevalia fields':             'https://i.imgur.com/piVz6YW.jpeg',
  'grand polo chevalia fields':  'https://i.imgur.com/piVz6YW.jpeg',
  'lia':                         'https://i.imgur.com/lqynarw.jpeg',
  'salva':                       'https://i.imgur.com/k9ny6Fr.jpeg',
  'the heights salva':           'https://i.imgur.com/k9ny6Fr.jpeg',
  'rena':                        'https://i.imgur.com/5f9HOrF.jpeg',
  'address residences':          'https://i.imgur.com/EneU4Ud.jpeg',
  'address residence':           'https://i.imgur.com/EneU4Ud.jpeg',
  'ocean star':                  'https://i.imgur.com/CdkYtCF.jpeg',
  'the edge':                    'https://i.imgur.com/Gzoo40q.jpeg',
  'wynwood':                     'https://i.imgur.com/Bup61sa.jpeg',
  'wynwood residences':          'https://i.imgur.com/Bup61sa.jpeg',
  'one park central':            'https://i.imgur.com/4lNc5Zb.jpeg',
  'samana lakes view':           'https://i.imgur.com/zhZ5t5s.jpeg',
   'clearpoint':                 'https://i.imgur.com/HhXIcMP.jpeg',
  'samana lakes':                'https://i.imgur.com/zhZ5t5s.jpeg',
  'damac bay':                   'https://i.imgur.com/mx8T6K3.jpeg',
  'azizi mirage':                'https://i.imgur.com/uC4rzLM.jpeg',
 'club drive':                   'https://i.imgur.com/8GVxtij.jpeg',
  'sterling':                    'https://i.imgur.com/jBpJvx2.jpeg',
  'aeon':                        'https://i.imgur.com/fQ8BnpN.jpeg',
  'saria':                        'https://i.imgur.com/OT6cYbn.jpeg',
  'seahaven':                        'https://i.imgur.com/Rvg80bH.jpeg',
  'verve':                        'https://i.imgur.com/yhqKBGN.jpeg',
  'nautica':                     'https://i.imgur.com/Qgn9Poh.jpeg',
  'creek vistas heights':          'https://i.imgur.com/U1UdSvR.jpeg',
   'vistas heights':          'https://i.imgur.com/U1UdSvR.jpeg',
  'creek waters 2':              'https://i.imgur.com/IBbv4Bb.jpeg',
  'binghatti':              'https://i.imgur.com/Qh35AiR.jpeg',
  'creek waters':                'https://i.imgur.com/IBbv4Bb.jpeg',
  'marina place 2':              'https://i.imgur.com/f2Ciq1v.jpeg',
  'royal atlantis':              'https://i.imgur.com/jTEqN2m.png',
  'empire heights':              'https://i.imgur.com/6xLTMnc.png',
  'jumeira business center':     'https://i.imgur.com/CKKxlsD.png',
  'jumeirah business center':    'https://i.imgur.com/CKKxlsD.png',
  'jumeira business centre':     'https://i.imgur.com/CKKxlsD.png',
  'jumeirah business centre':    'https://i.imgur.com/CKKxlsD.png',
};

const SQM_TO_SQFT_RATE = 10.764;
const SQFT_TO_SQM_RATE = 0.0929;
const AED_TO_EUR_RATE = 0.25;

const isLikelySqm = (area: number): boolean => {
  return area > 0 && area < 300;
};

const formatAreaDisplay = (area: string | number | undefined): { 
  primary: string; 
  secondary: string;
  sqft: number;
  sqm: number;
} => {
  const areaNum = typeof area === 'string' ? parseFloat(area) || 0 : (area || 0);
  if (areaNum === 0) return { primary: '-', secondary: '', sqft: 0, sqm: 0 };
  if (isLikelySqm(areaNum)) {
    const sqft = Math.round(areaNum * SQM_TO_SQFT_RATE);
    return {
      primary: `${areaNum.toLocaleString('en-AE', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} m²`,
      secondary: `${sqft.toLocaleString('en-AE')} sqft`,
      sqft: sqft,
      sqm: areaNum
    };
  } else {
    const sqm = areaNum * SQFT_TO_SQM_RATE;
    return {
      primary: `${Math.round(areaNum).toLocaleString('en-AE')} sqft`,
      secondary: `${sqm.toFixed(1)} m²`,
      sqft: areaNum,
      sqm: sqm
    };
  }
};

const formatPricePerArea = (purchaseValue: number | undefined, area: string | number | undefined): {
  aedPerSqft: string;
  eurPerSqm: string;
} => {
  const areaData = formatAreaDisplay(area);
  const price = purchaseValue || 0;
  if (price === 0 || areaData.sqft === 0) {
    return { aedPerSqft: '-', eurPerSqm: '' };
  }
  const aedPerSqft = Math.round(price / areaData.sqft);
  const eurPerSqm = Math.round((price * AED_TO_EUR_RATE) / areaData.sqm);
  return {
    aedPerSqft: `AED ${aedPerSqft.toLocaleString('en-AE')}/sqft`,
    eurPerSqm: `€${eurPerSqm.toLocaleString('de-DE')}/m²`
  };
};

// News age in the reader's language, from the article's own timestamp; the
// stored label ("1h ago") is English and only used when there is none.
const newsAge = (iso: string | undefined, fallback: string, de: boolean): string => {
  const t = iso ? Date.parse(iso) : NaN;
  if (isNaN(t)) return fallback;
  const h = Math.floor((Date.now() - t) / 3600000);
  if (h < 1) return de ? 'Gerade eben' : 'Just now';
  if (h < 24) return de ? `vor ${h} Std.` : `${h}h ago`;
  if (h < 48) return de ? 'Gestern' : 'Yesterday';
  return new Date(t).toLocaleDateString(de ? 'de-DE' : 'en-US', { day: 'numeric', month: 'short', year: 'numeric' });
};

const getProjectImage = (unit: Unit, dynamicImages: { [key: string]: string } = {}): string | null => {
  if (unit.image || unit.projectImage) return unit.image || unit.projectImage || null;
  const merged = { ...PROJECT_IMAGES, ...dynamicImages };
  const searchText = `${unit.project || ''} ${unit.name || ''} ${unit.unitNumber || ''}`.toLowerCase();
  for (const [key, url] of Object.entries(merged)) {
    if (url && searchText.includes(key)) {
      return url;
    }
  }
  return null;
};

const getFloorNumber = (unitNumber: string): string => {
  const matches = unitNumber?.match(/(\d{3,4})(?:\D*)?$/);
  const unitNum = matches ? matches[1] : '';
  if (unitNum.length === 3) return unitNum.charAt(0);
  if (unitNum.length === 4) return String(parseInt(unitNum.substring(0, 2)));
  return '-';
};

const getBedroomCount = (unit: Unit): string => {
  // Check explicit bedrooms field first
  const br = String(unit.bedrooms ?? '');
  const num = br.match(/\d+/);
  if (num && parseInt(num[0]) <= 20) return num[0];

  // Check if type contains bedroom info (e.g. "1 Bedroom", "2BR", "3 bed", "2 B/R")
  const typeStr = String(unit.type || '').toLowerCase();
  if (typeStr.includes('studio')) return 'Studio';

  // Match patterns: "2 B/R", "2BR", "2 bed", "2 bedroom", "2bed"
  const brMatch = typeStr.match(/(\d+)\s*(?:b\/?r|bed|bedroom)/i);
  if (brMatch) return brMatch[1];

  // Also check pattern like "Bedroom 1" or "B/R 2"
  const bedMatch2 = typeStr.match(/(?:b\/?r|bed|bedroom)\s*(\d+)/i);
  if (bedMatch2) return bedMatch2[1];

  // If type is just a number or contains sqft/sqm, it's area not bedrooms — skip it
  return '-';
};

const getCalendlyLink = (advisor: string | null | undefined): string => {
  if (!advisor) return 'https://calendly.com/d/cs9h-hxd-2zx/dubai-immobilien-gesprach-mit-investmenter';
  const advisorLower = advisor.toLowerCase();
  if (advisorLower.includes('jim')) return 'https://calendly.com/investmenterjim/60min';
  if (advisorLower.includes('kiano')) return 'https://calendly.com/kiano-loehr/immobilien-meeting';
  if (advisorLower.includes('brigitte')) return 'https://calendly.com/brigitte-menter/duabi-immobilien-gesprach-mit-brigitte-menter';
  if (advisorLower.includes('michael')) return 'https://calendly.com/michael-investmenter/dubai-immobilien-gesprach-mit-michael-menter';
  return 'https://calendly.com/d/cs9h-hxd-2zx/dubai-immobilien-gesprach-mit-investmenter';
};

const ImageSlideshow: React.FC<{ images: string[]; projectName: string }> = ({ images, projectName }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [images.length, isAutoPlaying]);

  const goToPrevious = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const goToNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const goToSlide = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setIsAutoPlaying(false);
    setCurrentIndex(index);
  };

  return (
    <div className="slideshow-container">
      <div className="slideshow-images">
        {images.map((img, index) => (
          <img
            key={index}
            src={img}
            alt={`${projectName} ${index + 1}`}
            className={`slideshow-image ${index === currentIndex ? 'active' : ''}`}
          />
        ))}
      </div>
      <button className="slide-arrow slide-arrow-left" onClick={goToPrevious}>
        <ChevronLeft size={20} />
      </button>
      <button className="slide-arrow slide-arrow-right" onClick={goToNext}>
        <ChevronRight size={20} />
      </button>
      <div className="slideshow-dots">
        {images.map((_, index) => (
          <button
            key={index}
            className={`dot ${index === currentIndex ? 'active' : ''}`}
            onClick={(e) => goToSlide(index, e)}
          />
        ))}
      </div>
      <div className="slide-counter">
        {currentIndex + 1} / {images.length}
      </div>
    </div>
  );
};

const VideoPlayer: React.FC<{ videoUrl: string; projectName: string }> = ({ videoUrl }) => {
  const { t } = useLanguage();
  // Convert imgur page links to direct video URLs
  let directUrl = videoUrl;
  if (videoUrl.includes('imgur.com/') && !videoUrl.includes('i.imgur.com')) {
    const imgurId = videoUrl.split('/').pop()?.split('.')[0]?.split('?')[0];
    if (imgurId) directUrl = `https://i.imgur.com/${imgurId}.mp4`;
  }

  // Check if it's an embeddable link (YouTube, Vimeo, etc.)
  const isYoutube = directUrl.includes('youtube.com') || directUrl.includes('youtu.be');
  const isVimeo = directUrl.includes('vimeo.com');

  if (isYoutube || isVimeo) {
    let embedUrl = directUrl;
    if (isYoutube) {
      const videoId = directUrl.match(/(?:v=|youtu\.be\/)([^&?/]+)/)?.[1];
      if (videoId) embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1`;
    }
    return (
      <div className="video-player-container">
        <iframe src={embedUrl} style={{ width: '100%', height: '100%', border: 'none' }} allow="autoplay; encrypted-media" allowFullScreen title={t('mdProjectVideo')} />
      </div>
    );
  }

  return (
    <div className="video-player-container">
      <video
        src={directUrl}
        autoPlay
        muted
        loop
        playsInline
        className="project-video"
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        onError={(e) => {
          const target = e.currentTarget;
          target.style.display = 'none';
          const fallback = document.createElement('div');
          fallback.style.cssText = 'display:flex;align-items:center;justify-content:center;height:100%;background:#f5f5f5;color:#999';
          fallback.textContent = t('mdVideoUnavailable');
          target.parentElement?.appendChild(fallback);
        }}
      >
        {t('mdVideoNotSupported')}
      </video>
    </div>
  );
};

const MainDashboard: React.FC<MainDashboardProps> = ({ 
  currentInvestor, 
  units, 
  apiUrl, 
  setActiveTab,
  totalPortfolioValue = 0,
  onOpenChat,
  unreadMessages = 0,
  pmUnitsOverride,
  dldOverride,
  onAskCopilot,
  onOpenWealth,
}) => {
  const { t, language } = useLanguage();

  // DLD valuations for the live value counter, cheapest source first:
  //   1. the Portfolio tab's in-memory payload, if it was fetched this session;
  //   2. the slim copy the last fetch saved on this device (instant on open);
  //   3. only if the device has none, or it is over a day old, a fetch —
  //      started AFTER the portfolio has loaded. Fired on app open it ran
  //      alongside getInvestorData on a cold backend and the portfolio showed
  //      AED 0 / "No Properties Yet" for 90s+ (observed 2026-09-29).
  const [dldIntel, setDldIntel] = useState<any>(() => {
    if (dldOverride) return dldOverride;
    const id = currentInvestor?.id;
    return id ? (cachedMarketIntelligence(id) || storedMarketIntelligence(id)) : null;
  });
  const [dldFetching, setDldFetching] = useState(false);
  const unitsLoaded = (units as any[]).length > 0;
  useEffect(() => {
    if (dldOverride) { setDldIntel(dldOverride); return; }
    const investorId = currentInvestor?.id;
    if (!investorId) return;
    const mem = cachedMarketIntelligence(investorId);
    if (mem) { setDldIntel(mem); return; }
    const stored = storedMarketIntelligence(investorId);
    if (stored) setDldIntel(stored);
    // A device copy younger than 3 minutes is used as is. An older one stays on
    // screen while a fresh copy is fetched in the background (2026-11-05): Home
    // used to refetch only when the device had NEVER been valued, so a corrected
    // value (an Oqood price, a re-keyed baseline) waited until someone opened
    // Portfolio. That rule dated from 2026-09-29, when one valuation cost ~70s of
    // CPU on a fresh backend; the saved server copy (dld_portfolio_cache) now
    // answers in about 2s, and the old figure is never replaced by AED 0.
    const token = localStorage.getItem('authToken');
    const storedFresh = !!stored && Date.now() - Number((stored as any).savedAt || 0) < 3 * 60 * 1000;
    if (storedFresh || !unitsLoaded || !apiUrl || !token) return;
    let alive = true;
    const timer = window.setTimeout(() => {
      if (!stored) setDldFetching(true);
      fetchMarketIntelligence(apiUrl, investorId, token)
        .then(d => { if (alive && d) setDldIntel(d); })
        .catch(() => { /* counter stays hidden */ })
        .finally(() => { if (alive) setDldFetching(false); });
    }, 3000);
    return () => { alive = false; window.clearTimeout(timer); };
  }, [currentInvestor?.id, dldOverride, unitsLoaded, apiUrl]);
  const growth = React.useMemo(() => dldValueGrowth(dldIntel, units as any[]), [dldIntel, units]);
  // The headline is what was PAID for the held units. Summed here from
  // purchaseValue rather than taken from totalPortfolioValue, which sums
  // currentValue — on Asset Desk units that is the purchase price, but not on
  // every record, and the caption must be true on all of them.
  const totalInvested = React.useMemo(() => {
    const sum = (units as any[]).reduce((s: number, u: any) =>
      (u?.status || '').toLowerCase() === 'sold' ? s : s + (Number(u?.purchaseValue) || 0), 0);
    return sum > 0 ? sum : totalPortfolioValue;
  }, [units, totalPortfolioValue]);
  const valueAt = React.useCallback(
    (ms: number) => growth
      ? growth.valueToday * Math.pow(1 + growth.rate, (ms - growth.asOf) / (365.25 * 86400000))
      : 0,
    [growth],
  );
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [expandedProperties, setExpandedProperties] = useState(false);
  const [primaryUnitId, setPrimaryUnitId] = useState<string | null>(() => {
    if (currentInvestor?.id) {
      return localStorage.getItem(`primaryUnit_${currentInvestor.id}`);
    }
    return null;
  });

  // Sort units so the pinned/primary unit is first
  const sortedUnits = React.useMemo(() => {
    if (!primaryUnitId || units.length <= 1) return units;
    const idx = units.findIndex(u => u.id === primaryUnitId);
    if (idx <= 0) return units; // already first or not found
    const copy = [...units];
    const [pinned] = copy.splice(idx, 1);
    copy.unshift(pinned);
    return copy;
  }, [units, primaryUnitId]);

  /* What the Properties section renders: the pinned unit first (that is what
     the pin button on each card is for), and the "documents without a
     property" folder last. The folder is not a property, so it must never
     become the headline card. Sort is stable, so the pin order survives. */
  const dashboardUnits = React.useMemo(
    () => [...sortedUnits].sort((a, b) =>
      Number((a?.name || '').toLowerCase() === 'other documents') -
      Number((b?.name || '').toLowerCase() === 'other documents')),
    [sortedUnits]
  );

  /* The headline count is properties only — the documents folder is not one. */
  const propertyCount = dashboardUnits.filter(
    u => (u?.name || '').toLowerCase() !== 'other documents'
  ).length;

  /* The line under the portfolio value has to describe the same units the value
     is built from. A sold unit's value left the portfolio with it, so counting
     it here would read as "AED 26.2m across 12 properties" when the figure only
     covers 10. */
  const valuedPropertyCount = dashboardUnits.filter(
    u => (u?.name || '').toLowerCase() !== 'other documents'
      && ((u as any)?.status || '').toLowerCase() !== 'sold'
  ).length;

  const handleSetPrimaryUnit = (unitId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const newId = primaryUnitId === unitId ? null : unitId;
    setPrimaryUnitId(newId);
    if (currentInvestor?.id) {
      if (newId) {
        localStorage.setItem(`primaryUnit_${currentInvestor.id}`, newId);
      } else {
        localStorage.removeItem(`primaryUnit_${currentInvestor.id}`);
      }
    }
  };
  
  const [showFeatureModal, setShowFeatureModal] = useState(false);
  const [featureMessage, setFeatureMessage] = useState('');
  const [featureSubmitting, setFeatureSubmitting] = useState(false);
  const [featureSuccess, setFeatureSuccess] = useState(false);
  const [featureError, setFeatureError] = useState<string | null>(null);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackHoverRating, setFeedbackHoverRating] = useState(0);
  const [feedbackContext, setFeedbackContext] = useState<string | null>(null);
  const [showContextualFeedback, setShowContextualFeedback] = useState(false);
  const [contextualFeedbackType, setContextualFeedbackType] = useState<string | null>(null);

  const [showDocRequestModal, setShowDocRequestModal] = useState(false);
  const [docRequestItems, setDocRequestItems] = useState<string[]>([]);
  const [docCustomText, setDocCustomText] = useState('');
  const [docSelectedUnit, setDocSelectedUnit] = useState('');
  const [docSubmitting, setDocSubmitting] = useState(false);
  const [docSuccess, setDocSuccess] = useState(false);
  const [docError, setDocError] = useState<string | null>(null);

  // --- What this investor qualifies for (the "Available to you" cards) ---
  // Same figure as the hero card: DLD value today, else what was paid.
  const heldPortfolioValue = growth ? growth.valueToday : totalInvested;
  const eligibleServices = React.useMemo(() => {
    const de = language === 'de';
    const ownsProperty = valuedPropertyCount > 0;
    const name = currentInvestor?.name || '';
    const list: { id: string; title: string; reason: string; cta: string; icon: React.ReactNode; onOpen: () => void }[] = [];
    if (ownsProperty) {
      list.push({
        id: 'golden-visa',
        title: 'Golden Visa',
        reason: de ? 'Sie besitzen eine Immobilie in Dubai' : 'You own property in Dubai',
        cta: de ? 'Per WhatsApp anfragen' : 'Ask on WhatsApp',
        icon: <Star size={22} />,
        onOpen: () => openWhatsApp(
          de
            ? `Hallo, ich interessiere mich für das Golden Visa.\n\nName: ${name}\nE-Mail: ${currentInvestor?.email || ''}\nImmobilien in Dubai: ${valuedPropertyCount}`
            : `Hello, I am interested in the Golden Visa.\n\nName: ${name}\nEmail: ${currentInvestor?.email || ''}\nProperties in Dubai: ${valuedPropertyCount}`,
          WHATSAPP_VISA_NUMBER,
        ),
      });
    }
    if (heldPortfolioValue >= 2_000_000) {
      list.push({
        id: 'company-formation',
        title: de ? 'Firmengründung in Dubai' : 'Company formation in Dubai',
        reason: de ? 'Ihr Portfolio liegt über AED 2 Mio.' : 'Your portfolio is above AED 2m',
        cta: de ? 'Termin mit Julia buchen' : 'Book a call with Julia',
        icon: <Building size={22} />,
        onOpen: () => { openCalendly({ name: currentInvestor?.name, email: currentInvestor?.email }); },
      });
    }
    if (ownsProperty) {
      list.push({
        id: 'tax-consultation',
        title: de ? 'Steuerberatung' : 'Tax consultation',
        reason: de ? 'Für Eigentümer von Immobilien in Dubai' : 'For owners of Dubai property',
        cta: de ? 'Termin mit Julia buchen' : 'Book a call with Julia',
        icon: <FileText size={22} />,
        onOpen: () => { openCalendly({ name: currentInvestor?.name, email: currentInvestor?.email }); },
      });
    }
    return list;
  }, [language, valuedPropertyCount, heldPortfolioValue, currentInvestor?.name, currentInvestor?.email]);

  // --- Services Section ---
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [conveyanceSubmitting, setConveyanceSubmitting] = useState(false);
  const [conveyanceSuccess, setConveyanceSuccess] = useState(false);
  const [conveyanceError, setConveyanceError] = useState<string | null>(null);

  // Rendered by the cards below and, pre-login, by portals/ServicesShowcase.
  const services: ServiceItem[] = SERVICES;


  const handleServiceEnquiry = async (serviceName: string) => {
    setConveyanceSubmitting(true);
    setConveyanceError(null);
    try {
      const response = await fetch(`${apiUrl}/service-enquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: currentInvestor?.name || 'Unknown',
          email: currentInvestor?.email || '',
          service: serviceName,
          investorId: currentInvestor?.id || undefined,
        }),
      });
      if (response.ok) {
        setConveyanceSuccess(true);
        setTimeout(() => {
          setConveyanceSuccess(false);
                    setSelectedService(null);
        }, 3000);
      } else {
        throw new Error('Failed');
      }
    } catch {
      setConveyanceError(language === 'de' ? 'Fehler beim Senden. Bitte versuchen Sie es erneut.' : 'Failed to submit. Please try again.');
    } finally {
      setConveyanceSubmitting(false);
    }
  };

  // --- New Listings from Sell Platform ---
  interface PlatformListing {
    id: string;
    title: string | null;
    propertyType: string;
    location: string;
    sizeSqft: number | null;
    bedrooms: number | null;
    askingPrice: number | null;
    marketValue: number | null;
    isDistress: boolean;
    discountPercent: number | null;
    discountBase: 'market' | null;
    description: string | null;
    urgency: string | null;
    photoUrl: string | null;
    createdAt: string;
  }
  const [newListings, setNewListings] = useState<PlatformListing[]>([]);
  const [listingsLoading, setListingsLoading] = useState(false);
  const [enquiryListing, setEnquiryListing] = useState<PlatformListing | null>(null);
  const [enquirySubmitting, setEnquirySubmitting] = useState(false);
  const [enquiryDone, setEnquiryDone] = useState<Record<string, boolean>>({});

  useEffect(() => {
    // Blocked in a demo session, and the strip hides itself when the list is
    // empty — so Dubai Sell simply vanished from the dashboard.
    if (currentInvestor?.isDemo || isFilmMode()) {
      setNewListings(DEMO_PLATFORM_LISTINGS.map((l: any) => (
        language === 'de'
          ? { ...l, title: l.titleDe || l.title, description: l.descriptionDe || l.description }
          : l
      )));
      setListingsLoading(false);
      return;
    }
    if (!apiUrl) return;
    setListingsLoading(true);
    fetch(`${apiUrl.replace(/\/api\/?$/, '')}/api/sell-platform/listings?limit=50`)
      .then(r => r.ok ? r.json() : { listings: [] })
      .then(data => setNewListings(data.listings || []))
      .catch(() => setNewListings([]))
      .finally(() => setListingsLoading(false));
  }, [apiUrl]);

  const propertyTypeIcons: Record<string, React.ReactNode> = {
    apartment: <Building2 size={16} />, villa: <Home size={16} />, penthouse: <Building size={16} />, townhouse: <Building2 size={16} />, commercial: <Store size={16} />
  };

  const propertyTypeImages: Record<string, string> = {
    apartment: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=600&h=400&fit=crop',
    villa: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=600&h=400&fit=crop',
    penthouse: 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=600&h=400&fit=crop',
    townhouse: 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=600&h=400&fit=crop',
    commercial: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600&h=400&fit=crop'
  };

  const urgencyLabel = (u: string | null): string => {
    if (u === '3days') return appLanguage() === 'de' ? 'Dringend (3 Tage)' : 'ASAP (3 days)';
    if (u === '1-3months') return appLanguage() === 'de' ? '1–3 Monate' : '1–3 Months';
    return appLanguage() === 'de' ? 'Unverbindlich' : 'Exploring';
  };

  const handleEnquiry = async (listing: PlatformListing) => {
    if (!currentInvestor) return;
    setEnquirySubmitting(true);
    try {
      const baseUrl = apiUrl.replace(/\/api\/?$/, '');
      const res = await fetch(`${baseUrl}/api/sell-platform/enquire`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          listingId: listing.id,
          propertyType: listing.propertyType,
          location: listing.location,
          askingPrice: listing.askingPrice,
          bedrooms: listing.bedrooms,
          sizeSqft: listing.sizeSqft,
          buyerName: currentInvestor.name,
          buyerEmail: currentInvestor.email,
          buyerPhone: (currentInvestor as any).phone || '',
        })
      });
      if (res.ok) {
        setEnquiryDone(prev => ({ ...prev, [listing.id]: true }));
      }
    } catch (err) {
      console.error('Enquiry error:', err);
    } finally {
      setEnquirySubmitting(false);
      setEnquiryListing(null);
    }
  };

  const getGreeting = (): string => {
    const hour = new Date().getHours();
    if (hour < 12) return t('goodMorning');
    if (hour < 17) return t('goodAfternoon');
    return t('goodEvening');
  };

  const fullName = currentInvestor?.name || t('investor');
  const initials = fullName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  // First name only in the greeting ("Good evening, Jim") — the full name was
  // cut to "Jim Mac ..." beside the header buttons on a phone.
  const firstName = fullName.trim().split(/\s+/)[0] || fullName;
  const displayName = firstName.length > 16 ? firstName.substring(0, 14) + '...' : firstName;
  
  const investmentAdvisor = currentInvestor?.investmentAdvisor || currentInvestor?.advisor || currentInvestor?.investorAdvisor || null;

  // To-dos ticked off on Home. Kept on the device as well as on the server, so
  // a tick shows at once and survives a server that has not caught up yet
  // (the bell polls every 10s and would otherwise bring the item back).
  const doneKey = `todoDone_${currentInvestor?.id || ''}`;
  const [doneIds, setDoneIds] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem(doneKey) || '[]'); } catch { return []; }
  });
  useEffect(() => {
    try { setDoneIds(JSON.parse(localStorage.getItem(doneKey) || '[]')); } catch { setDoneIds([]); }
  }, [doneKey]);
  // Takes every id behind one row: a reminder stored twice is still one to-do.
  const markDone = (ids: string[]) => {
    setDoneIds(prev => {
      const next = [...prev, ...ids.filter(id => !prev.includes(id))].slice(-300);
      try { localStorage.setItem(doneKey, JSON.stringify(next)); } catch { /* storage blocked */ }
      return next;
    });
    setNotifications(prev => prev.map(n => ids.includes(n.id) ? { ...n, done: true, read: true } : n));
    if (currentInvestor?.isDemo || isFilmMode()) return;
    ids.forEach(id => saveTodoDone(apiUrl, id, true));
  };
  const [showAllTodos, setShowAllTodos] = useState(false);

  // This month's instalments, shown with the reminders. Fetched once per
  // session — the plans change only when a document is approved.
  const [paymentTodos, setPaymentTodos] = useState<any[]>([]);
  useEffect(() => {
    if (!currentInvestor?.id || currentInvestor?.isDemo || isFilmMode() || !apiUrl) return;
    let live = true;
    fetchPaymentTodos(apiUrl, currentInvestor.id)
      .then(list => { if (live) setPaymentTodos(list); })
      .catch(() => { /* no plans, no payment rows */ });
    return () => { live = false; };
  }, [currentInvestor?.id, apiUrl]);

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  const handleFeatureSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!featureMessage.trim() && feedbackRating === 0) return;
    setFeatureSubmitting(true);
    setFeatureError(null);
    try {
      const authToken = localStorage.getItem('authToken');
      const response = await fetch(`${apiUrl}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
        },
        body: JSON.stringify({
          message: featureMessage.trim(),
          rating: feedbackRating || undefined,
          context: feedbackContext || undefined,
          investorName: fullName,
          investorEmail: currentInvestor?.email,
          submittedAt: new Date().toISOString()
        })
      });
      if (response.ok) {
        setFeatureSuccess(true);
        setTimeout(() => {
          setShowFeatureModal(false);
          setFeatureSuccess(false);
          setFeatureMessage('');
          setFeedbackRating(0);
          setFeedbackContext(null);
          setShowContextualFeedback(false);
          setContextualFeedbackType(null);
        }, 2000);
      } else {
        throw new Error('Failed to submit');
      }
    } catch (err) {
      setFeatureError(language === 'de'
        ? 'Fehler beim Senden. Bitte versuchen Sie es erneut.'
        : 'Failed to submit. Please try again.');
    } finally {
      setFeatureSubmitting(false);
    }
  };

  const openFeedbackWithContext = (context: string, type: string) => {
    setFeedbackContext(context);
    setContextualFeedbackType(type);
    setShowFeatureModal(true);
  };

  const handleQuickRating = async (rating: number, context: string) => {
    try {
      const authToken = localStorage.getItem('authToken');
      await fetch(`${apiUrl}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
        },
        body: JSON.stringify({
          message: '',
          rating,
          context,
          investorName: fullName,
          investorEmail: currentInvestor?.email,
          submittedAt: new Date().toISOString()
        })
      });
    } catch (err) {
      // Silent fail for quick ratings
    }
  };

  const [upcomingProjects, setUpcomingProjects] = useState<UpcomingProject[]>([]);
  const [dynamicProjectImages, setDynamicProjectImages] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    fetch(`${apiUrl}/project-images`)
      .then(r => r.ok ? r.json() : {})
      .then(data => setDynamicProjectImages(data || {}))
      .catch(() => {});
  }, [apiUrl]);
  
  useEffect(() => {
    // A demo or film session has no backend to ask — its API traffic is blocked
    // — so the bell served nothing, and "how does the app tell me about things"
    // is the first question anyone watching asks. See demoNotifications.
    if (currentInvestor?.isDemo || isFilmMode()) {
      setNotifications(DEMO_NOTIFICATIONS.map((n: any) => ({ ...n, timestamp: new Date(n.timestamp) })));
      setUnreadCount(DEMO_NOTIFICATIONS.filter((n: any) => !n.read).length);
      return;
    }

    const fetchNotifications = async () => {
      try {
        const authToken = localStorage.getItem('authToken');
        const url = `${apiUrl}/notifications/${encodeURIComponent(currentInvestor?.id || '')}`;
        const response = await fetch(url, {
          headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
        });
        if (response.ok) {
          const data = await response.json();
          setNotifications(data.map((n: any) => ({
            ...n,
            timestamp: new Date(n.timestamp)
          })));
          setUnreadCount(data.filter((n: any) => !n.read).length);
        }
      } catch (error) {
        console.error('Failed to fetch notifications:', error);
      }
    };
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 10000);
    return () => clearInterval(interval);
  }, [currentInvestor?.id, apiUrl]);

  useEffect(() => {
    // Same again: unreachable in a demo, so the heading rendered with nothing
    // under it.
    if (currentInvestor?.isDemo || isFilmMode()) {
      setUpcomingProjects(DEMO_UPCOMING_PROJECTS as any);
      return;
    }

    const fetchProjects = async () => {
      try {
        const response = await fetch(`${apiUrl}/upcoming-projects`);
        if (response.ok) {
          const data = await response.json();
          setUpcomingProjects(data.filter((p: any) => p.status === 'active' || !p.status));
        }
      } catch (error) {
        console.error('Failed to fetch projects:', error);
      }
    };
    fetchProjects();
  }, [apiUrl]);

  // Handle feedback URL params (from portfolio email links)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('feedback') === '1') {
      const rating = parseInt(params.get('rating') || '0');
      const context = params.get('context') || 'portfolio_email';
      if (rating >= 1 && rating <= 5) {
        setFeedbackRating(rating);
      }
      setFeedbackContext(context);
      setContextualFeedbackType('portfolio');
      setShowFeatureModal(true);
      // Clean URL without reload
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Contextual feedback: show after user has been on dashboard for 60 seconds (once per 14 days)
  useEffect(() => {
    const lastShown = localStorage.getItem('ctxFeedbackDismissed_general');
    const daysSince = lastShown ? (Date.now() - parseInt(lastShown)) / (1000 * 60 * 60 * 24) : 999;
    if (daysSince < 14) return;
    // Only show if investor has properties (engaged user)
    if (!units || units.length === 0) return;
    const timer = setTimeout(() => {
      if (!showFeatureModal) {
        setContextualFeedbackType('portfolio');
        setShowContextualFeedback(true);
      }
    }, 60000);
    return () => clearTimeout(timer);
  }, [units, showFeatureModal]);

  const handleDocumentRequest = () => {
    setShowDocRequestModal(true);
    setDocRequestItems([]);
    setDocCustomText('');
    setDocSelectedUnit(units.length === 1 ? `${units[0].project} - ${units[0].unitNumber}` : '');
    setDocSuccess(false);
    setDocError(null);
  };

  const docTypeOptions = [
    { id: 'spa', label: language === 'de' ? 'Kaufvertrag (SPA)' : 'Sale & Purchase Agreement (SPA)' },
    { id: 'oqood', label: appLanguage() === 'de' ? 'Oqood / Eigentumsurkunde' : 'Oqood / Title Deed' },
    { id: 'noc', label: appLanguage() === 'de' ? 'NOC (Unbedenklichkeitsbescheinigung)' : 'NOC (No Objection Certificate)' },
    { id: 'payment_plan', label: language === 'de' ? 'Zahlungsplan' : 'Payment Plan' },
    { id: 'receipt', label: language === 'de' ? 'Zahlungsbeleg' : 'Payment Receipt' },
    { id: 'handover', label: language === 'de' ? 'Übergabedokument' : 'Handover Letter' },
    { id: 'floor_plan', label: language === 'de' ? 'Grundriss' : 'Floor Plan' },
  ];

  const toggleDocItem = (label: string) => {
    setDocRequestItems(prev =>
      prev.includes(label) ? prev.filter(i => i !== label) : [...prev, label]
    );
  };

  const handleDocRequestSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const allDocs = [...docRequestItems];
    if (docCustomText.trim()) allDocs.push(docCustomText.trim());
    if (allDocs.length === 0 || !docSelectedUnit) return;

    setDocSubmitting(true);
    setDocError(null);
    try {
      const response = await fetch(`${apiUrl}/api/document-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          documents: allDocs,
          unit: docSelectedUnit,
          investorName: fullName,
          investorEmail: currentInvestor?.email,
          investorId: currentInvestor?.id || null,
          submittedAt: new Date().toISOString()
        })
      });
      if (response.ok) {
        setDocSuccess(true);
        setTimeout(() => {
          setShowDocRequestModal(false);
          setDocSuccess(false);
          setDocRequestItems([]);
          setDocCustomText('');
          setDocSelectedUnit('');
        }, 2000);
      } else {
        throw new Error('Failed to submit');
      }
    } catch (err) {
      setDocError(language === 'de'
        ? 'Fehler beim Senden. Bitte versuchen Sie es erneut.'
        : 'Failed to submit. Please try again.');
    } finally {
      setDocSubmitting(false);
    }
  };

  const requestActions = [
    { id: 'document', label: t('documentRequest'), icon: <FileText size={22} />, action: handleDocumentRequest },
    { id: 'service', label: t('serviceRequest'), icon: <BellRing size={22} />, action: () => setActiveTab('marketplace') },
    { id: 'contact', label: t('pointOfContact'), icon: <Phone size={22} />, action: () => window.open('https://wa.me/971508418876', '_blank') },
    { id: 'feedback', label: language === 'de' ? 'Feedback' : 'Feedback', icon: <Lightbulb size={22} />, action: () => setShowFeatureModal(true) },
  ];

  const [newsItems, setNewsItems] = useState([
    { id: '1', title: 'Dubai Property Market Hits AED 682B Record', summary: '2025 sales surge 30% YoY, marking fifth consecutive record-breaking year', date: 'Jan 2026', url: 'https://gulfnews.com/business/property' },
    { id: '2', title: 'Villa Prices Soar 206% Since Pandemic', summary: 'Average freehold villa values surpass 2014 peak by 86%', date: 'Jan 2026', url: 'https://gulfnews.com/business/property' },
    { id: '3', title: 'Bugatti Penthouse Sells for AED 550M', summary: 'Record-breaking sale at AED 11,650/sqft in Business Bay', date: 'Dec 2025', url: 'https://hauteliving.com' }
  ]);

  useEffect(() => {
    // Same reason as the notifications above: /dubai-news is unreachable in a
    // demo session, and the hardcoded fallback below it is dated Jan 2026.
    if (currentInvestor?.isDemo || isFilmMode()) {
      // The fixture is written German-first with the English alongside; the
      // strip renders title/summary, so pick the pair for the current language.
      setNewsItems(DEMO_NEWS.map((n: any) => (
        language === 'de' ? n : { ...n, title: n.titleEn || n.title, summary: n.summaryEn || n.summary }
      )));
      return;
    }

    const fetchNews = async () => {
      try {
        const response = await fetch(`${apiUrl}/dubai-news`);
        if (response.ok) {
          const data = await response.json();
          if (data && data.length > 0) setNewsItems(data);
        }
      } catch (error) {
      }
    };
    fetchNews();
    // `language` matters here: the sample strip above is picked per language,
    // so switching to German mid-session has to re-select it.
  }, [apiUrl, currentInvestor?.isDemo, language]);

  const PropertyCard: React.FC<{ unit: Unit }> = ({ unit }) => {
    const areaDisplay = formatAreaDisplay(unit.area);
    const pricePerArea = formatPricePerArea(unit.purchaseValue, unit.area);

    // Special card for "other documents" units
    if (unit.name?.toLowerCase() === 'other documents') {
      return (
        <div className="property-card-detailed" onClick={() => setActiveTab('portfolio')} style={{
          background: '#fff',
          borderRadius: '16px',
          padding: '16px',
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          marginBottom: '12px',
          border: '1px dashed #d1d5db',
          display: 'flex',
          alignItems: 'center',
          gap: '14px'
        }}>
          <div style={{
            width: '56px',
            minWidth: '56px',
            height: '56px',
            borderRadius: '12px',
            background: '#f3f4f6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <FolderOpen size={26} color="#9ca3af" />
          </div>
          <div style={{ flex: 1 }}>
            <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#374151', margin: '0 0 2px 0' }}>
              {language === 'de' ? 'Dokumente ohne Immobilie' : 'Documents without a property'}
            </h3>
            <p style={{ fontSize: '14px', color: '#9ca3af', margin: 0 }}>
              {language === 'de'
                ? 'Hier landen Dokumente, die zu keiner Ihrer Immobilien passen'
                : "Files we can't match to one of your properties land here"}
            </p>
          </div>
          <ChevronRight size={18} color="#d1d5db" />
        </div>
      );
    }

    return (
      <div className="property-card-detailed" onClick={() => setActiveTab('portfolio')} style={{
        background: '#fff',
        borderRadius: '16px',
        padding: '16px',
        cursor: 'pointer',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        marginBottom: '12px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', margin: '0 0 12px 0', position: 'relative' }}>
          <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#1a1a1a', margin: 0, textAlign: 'center' }}>
            {unit.name ? `${unit.name} (${unit.unitNumber})` : (unit.unitNumber?.toLowerCase().includes(unit.project?.toLowerCase()) ? unit.unitNumber : `${unit.project} ${unit.unitNumber}`)}
          </h3>
          {units.length > 1 && (
            <button
              onClick={(e) => handleSetPrimaryUnit(unit.id, e)}
              title={primaryUnitId === unit.id
                ? (language === 'de' ? 'Nicht mehr als Hauptimmobilie anzeigen' : 'Unpin from top')
                : (language === 'de' ? 'Als Hauptimmobilie anzeigen' : 'Pin to top')}
              style={{
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Star
                size={18}
                color="#D6B160"
                fill={primaryUnitId === unit.id ? '#D6B160' : 'none'}
                strokeWidth={2}
              />
            </button>
          )}
        </div>
        
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
          <div style={{
            width: '90px', height: '90px', borderRadius: '12px', overflow: 'hidden', flexShrink: 0,
            background: 'linear-gradient(135deg, #D6B160 0%, #c9a050 100%)'
          }}>
            {(() => {
              const imageUrl = getProjectImage(unit, dynamicProjectImages);
              return imageUrl ? (
                <img src={imageUrl} alt={unit.project} style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={(e) => {
                    const img = e.target as HTMLImageElement;
                    img.style.display = 'none';
                    const fallback = img.parentElement;
                    if (fallback) {
                      fallback.innerHTML = '<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center"><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg></div>';
                    }
                  }} />
              ) : (
                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Building2 size={28} color="#fff" />
                </div>
              );
            })()}
          </div>
          
          <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
            <div>
              <span style={{ fontSize: '13.5px', color: '#999', display: 'block' }}>{language === 'de' ? 'Kaufdatum' : 'Purchase Date'}</span>
              <span style={{ fontSize: '14.5px', fontWeight: 600, color: '#1a1a1a' }}>
                {unit.purchaseDate ? new Date(unit.purchaseDate).toLocaleDateString(appLocale(), { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
              </span>
            </div>
            <div>
              <span style={{ fontSize: '13.5px', color: '#999', display: 'block' }}>{language === 'de' ? 'Kaufpreis' : 'Purchase Price'}</span>
              <span style={{ fontSize: '14.5px', fontWeight: 600, color: '#1a1a1a' }}>{unit.purchaseValue ? `AED ${unit.purchaseValue.toLocaleString()}` : '-'}</span>
            </div>
            <div>
              <span style={{ fontSize: '13.5px', color: '#999', display: 'block' }}>{language === 'de' ? 'Fläche' : 'Area'}</span>
              <span style={{ fontSize: '14.5px', fontWeight: 600, color: '#1a1a1a' }}>{areaDisplay.primary}</span>
              {areaDisplay.secondary && <span style={{ fontSize: '13px', color: '#888', display: 'block', marginTop: '1px' }}>≈ {areaDisplay.secondary}</span>}
            </div>
            <div>
              <span style={{ fontSize: '13.5px', color: '#999', display: 'block' }}>{language === 'de' ? 'Preis/Fläche' : 'Price/Area'}</span>
              <span style={{ fontSize: '14.5px', fontWeight: 600, color: '#1a1a1a' }}>{pricePerArea.aedPerSqft}</span>
              {pricePerArea.eurPerSqm && <span style={{ fontSize: '13px', color: '#888', display: 'block', marginTop: '1px' }}>≈ {pricePerArea.eurPerSqm}</span>}
            </div>
            <div>
              <span style={{ fontSize: '13.5px', color: '#999', display: 'block' }}>{language === 'de' ? 'Schlafzimmer' : 'Bedrooms'}</span>
              <span style={{ fontSize: '14.5px', fontWeight: 600, color: '#1a1a1a' }}>{getBedroomCount(unit)}</span>
            </div>
            <div>
              <span style={{ fontSize: '13.5px', color: '#999', display: 'block' }}>{language === 'de' ? 'Etage' : 'Floor'}</span>
              <span style={{ fontSize: '14.5px', fontWeight: 600, color: '#1a1a1a' }}>{getFloorNumber(unit.unitNumber)}</span>
            </div>
          </div>
        </div>
        
{(!unit.purchaseValue || unit.purchaseValue === 0) && (
          <div style={{
            marginTop: '10px',
            padding: '8px 12px',
            background: '#FFF8E1',
            borderRadius: '8px',
            border: '1px solid #FFE082',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <FileText size={16} />
            <span style={{ fontSize: '13.5px', color: '#92400e', fontWeight: 500 }}>
              {language === 'de' 
                ? 'Dokumente hochladen, um Immobiliendaten zu aktualisieren' 
                : 'Upload documents to update your property data'}
            </span>
          </div>
        )}
        
        {/* Construction Progress Bar */}
        {unit.constructionCompletionPercent != null && unit.constructionCompletionPercent < 100 && (
          <div style={{ marginTop: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
              <span style={{ fontSize: '13.5px', color: '#999', textTransform: 'uppercase' }}>{language === 'de' ? 'Baufortschritt' : 'Construction'}</span>
              <span style={{ fontSize: '14px', fontWeight: 700, color: unit.constructionCompletionPercent >= 75 ? '#10b981' : unit.constructionCompletionPercent >= 40 ? '#f59e0b' : '#ef4444' }}>
                {unit.constructionCompletionPercent}%
              </span>
            </div>
            <div style={{ width: '100%', height: '6px', backgroundColor: '#f3f4f6', borderRadius: '9999px', overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                backgroundColor: unit.constructionCompletionPercent >= 75 ? '#10b981' : unit.constructionCompletionPercent >= 40 ? '#f59e0b' : '#ef4444',
                borderRadius: '9999px',
                transition: 'width 0.7s ease-out',
                width: `${Math.min(100, Math.max(0, unit.constructionCompletionPercent))}%`
              }} />
            </div>
          </div>
        )}
        {unit.constructionCompletionPercent != null && unit.constructionCompletionPercent >= 100 && (
          <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <CheckCircle size={14} color="#065f46" />
            <span style={{ fontSize: '13.5px', fontWeight: 600, color: '#065f46' }}>{language === 'de' ? 'Bau abgeschlossen' : 'Construction Completed'}</span>
          </div>
        )}

        <div style={{ marginTop: '12px', textAlign: 'center' }}>
          <span style={{ color: '#D6B160', fontSize: '14.5px', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            {t('viewDetails')} <ChevronRight size={14} />
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="im-dashboard">
      <style>{`
        .property-image-small {
          flex-shrink: 0; width: 48px; height: 48px; border-radius: 8px; overflow: hidden;
          background: linear-gradient(135deg, #D6B160 0%, #c9a050 100%);
          display: flex; align-items: center; justify-content: center;
        }
        .property-image-small img { width: 100%; height: 100%; object-fit: cover; }
        .placeholder-image-small { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; }
      `}</style>

      {showFeatureModal && (
        <>
          <div className="fr-overlay" onClick={() => setShowFeatureModal(false)} />
          <div className="fr-modal">
            <div className="fr-header">
              <h2>{language === 'de' ? 'Feedback & Ideen' : 'Feedback & Ideas'}</h2>
              <button className="fr-close" onClick={() => setShowFeatureModal(false)}><X size={20} /></button>
            </div>
            {featureSuccess ? (
              <div className="fr-success">
                <CheckCircle size={48} color="#22c55e" />
                <h3>{language === 'de' ? 'Vielen Dank!' : 'Thank You!'}</h3>
                <p>{language === 'de' ? 'Wir haben Ihre Nachricht erhalten.' : 'We received your message.'}</p>
              </div>
            ) : (
              <form onSubmit={handleFeatureSubmit} className="fr-form">
                {feedbackContext && (
                  <div className="fr-context-badge">
                    {contextualFeedbackType === 'portfolio' && (language === 'de' ? 'Portfolio-Feedback' : 'Portfolio Feedback')}
                    {contextualFeedbackType === 'document' && (language === 'de' ? 'Dokument-Feedback' : 'Document Feedback')}
                    {contextualFeedbackType === 'calculator' && (language === 'de' ? 'Rechner-Feedback' : 'Calculator Feedback')}
                    {contextualFeedbackType === 'general' && (language === 'de' ? 'Allgemeines Feedback' : 'General Feedback')}
                    {!contextualFeedbackType && feedbackContext}
                  </div>
                )}
                <p className="fr-subtitle">
                  {language === 'de' ? 'Wie bewerten Sie Ihre Erfahrung?' : 'How would you rate your experience?'}
                </p>
                <div className="fr-stars">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      className="fr-star-btn"
                      onMouseEnter={() => setFeedbackHoverRating(star)}
                      onMouseLeave={() => setFeedbackHoverRating(0)}
                      onClick={() => setFeedbackRating(star)}
                    >
                      <Star
                        size={36}
                        fill={(feedbackHoverRating || feedbackRating) >= star ? '#D6B160' : 'transparent'}
                        color={(feedbackHoverRating || feedbackRating) >= star ? '#D6B160' : '#555'}
                        strokeWidth={1.5}
                      />
                    </button>
                  ))}
                </div>
                <div className="fr-star-labels">
                  <span>{language === 'de' ? 'Schlecht' : 'Poor'}</span>
                  <span>{language === 'de' ? 'Ausgezeichnet' : 'Excellent'}</span>
                </div>
                <p className="fr-subtitle" style={{ marginTop: 16 }}>
                  {language === 'de' ? 'Möchten Sie mehr erzählen? (optional)' : 'Want to tell us more? (optional)'}
                </p>
                <textarea value={featureMessage} onChange={(e) => setFeatureMessage(e.target.value)}
                  placeholder={language === 'de' ? 'Beschreiben Sie Ihre Idee oder Ihr Feedback...' : 'Describe your idea or feedback...'}
                  rows={4} maxLength={1000} />
                <span className="fr-count">{featureMessage.length}/1000</span>
                {featureError && <div className="fr-error">{featureError}</div>}
                <button type="submit" className="fr-submit" disabled={featureSubmitting || (feedbackRating === 0 && !featureMessage.trim())}>
                  {featureSubmitting ? <span className="fr-spinner" /> : <><Send size={16} />{language === 'de' ? 'Absenden' : 'Submit'}</>}
                </button>
              </form>
            )}
          </div>
          <style>{`
            .fr-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.7); z-index: 1000; backdrop-filter: blur(4px); }
            .fr-modal { position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); background: #1a1a1a; border-radius: 16px; width: 90%; max-width: 400px; z-index: 1001; border: 1px solid rgba(214,177,96,0.2); overflow: hidden; }
            .fr-header { display: flex; justify-content: space-between; align-items: center; padding: 18px 20px; border-bottom: 1px solid rgba(255,255,255,0.1); }
            .fr-header h2 { margin: 0; font-size: 17px; color: #fff; }
            .fr-close { background: rgba(255,255,255,0.1); border: none; border-radius: 8px; padding: 6px; cursor: pointer; color: #999; }
            .fr-form { padding: 20px; }
            .fr-subtitle { color: #999; font-size: 14px; margin: 0 0 16px; line-height: 1.5; }
            .fr-form textarea { width: 100%; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); border-radius: 12px; padding: 14px; color: #fff; font-size: 15px; resize: none; box-sizing: border-box; font-family: inherit; }
            .fr-form textarea:focus { outline: none; border-color: #D6B160; }
            .fr-form textarea::placeholder { color: #666; }
            .fr-count { display: block; text-align: right; font-size: 12px; color: #666; margin: 6px 0 16px; }
            .fr-error { background: rgba(239,68,68,0.1); border: 1px solid rgba(239,68,68,0.3); color: #ef4444; padding: 10px; border-radius: 8px; margin-bottom: 16px; font-size: 14px; }
            .fr-submit { width: 100%; padding: 14px; background: linear-gradient(135deg, #D6B160, #c9a050); border: none; border-radius: 12px; color: #000; font-size: 15px; font-weight: 600; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px; }
            .fr-submit:disabled { opacity: 0.5; cursor: not-allowed; }
            .fr-spinner { width: 18px; height: 18px; border: 2px solid transparent; border-top-color: #000; border-radius: 50%; animation: frspin 0.8s linear infinite; }
            @keyframes frspin { to { transform: rotate(360deg); } }
            .fr-success { padding: 40px 20px; text-align: center; }
            .fr-success h3 { color: #fff; margin: 16px 0 8px; }
            .fr-success p { color: #999; margin: 0; }
            .fr-stars { display: flex; justify-content: center; gap: 8px; margin: 12px 0 4px; }
            .fr-star-btn { background: none; border: none; cursor: pointer; padding: 4px; transition: transform 0.15s ease; }
            .fr-star-btn:active { transform: scale(1.2); }
            .fr-star-labels { display: flex; justify-content: space-between; padding: 0 8px; margin-bottom: 8px; }
            .fr-star-labels span { font-size: 11px; color: #666; }
            .fr-context-badge { background: rgba(214,177,96,0.15); border: 1px solid rgba(214,177,96,0.3); border-radius: 8px; padding: 8px 12px; margin-bottom: 14px; color: #D6B160; font-size: 13px; font-weight: 500; text-align: center; }
          `}</style>
        </>
      )}

      {showDocRequestModal && (
        <>
          <div className="dr-fullscreen">
            {/* Header */}
            <div className="dr-header">
              <button className="dr-back" onClick={() => setShowDocRequestModal(false)}>
                <X size={22} />
              </button>
              <h1>{language === 'de' ? 'Dokument anfordern' : 'Request Document'}</h1>
              <div style={{ width: 40 }} />
            </div>

            {docSuccess ? (
              <div className="dr-success-view">
                <div className="dr-success-icon">
                  <CheckCircle size={64} color="#D6B160" />
                </div>
                <h2>{language === 'de' ? 'Anfrage gesendet' : 'Request Submitted'}</h2>
                <p>{language === 'de' ? 'Unser Team wird sich in Kürze bei Ihnen melden.' : 'Our team will get back to you shortly.'}</p>
              </div>
            ) : (
              <form onSubmit={handleDocRequestSubmit} className="dr-body">
                {/* Unit Selector */}
                <div className="dr-section">
                  <label className="dr-section-label">{language === 'de' ? 'Einheit' : 'Property Unit'}</label>
                  {units.length > 0 ? (
                    <select
                      className="dr-unit-dropdown"
                      value={docSelectedUnit}
                      onChange={(e) => setDocSelectedUnit(e.target.value)}
                    >
                      <option value="">{language === 'de' ? 'Einheit auswählen...' : 'Select a unit...'}</option>
                      {units.map(u => {
                        const unitVal = `${u.project} - ${u.unitNumber}`;
                        return (
                          <option key={u.id} value={unitVal}>
                            {u.project} - {appLanguage() === 'de' ? 'Einheit' : 'Unit'} {u.unitNumber}
                          </option>
                        );
                      })}
                    </select>
                  ) : (
                    <div className="dr-no-units">
                      <p>{language === 'de' ? 'Keine Einheiten verfügbar' : 'No units available'}</p>
                    </div>
                  )}
                </div>

                {/* Document Types */}
                <div className="dr-section">
                  <label className="dr-section-label">{language === 'de' ? 'Dokumente auswählen' : 'Select Documents'}</label>
                  <div className="dr-doc-grid">
                    {docTypeOptions.map(opt => {
                      const isChecked = docRequestItems.includes(opt.label);
                      return (
                        <button type="button" key={opt.id} className={`dr-doc-chip ${isChecked ? 'dr-doc-active' : ''}`}
                          onClick={() => toggleDocItem(opt.label)}>
                          {isChecked && <CheckCircle size={16} />}
                          <span>{opt.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Notes */}
                <div className="dr-section">
                  <label className="dr-section-label">{language === 'de' ? 'Anmerkungen' : 'Additional Notes'}</label>
                  <textarea className="dr-textarea" value={docCustomText} onChange={(e) => setDocCustomText(e.target.value)}
                    placeholder={language === 'de' ? 'Optionale Anmerkungen...' : 'Optional notes...'}
                    rows={3} maxLength={500} />
                  <span className="dr-char-count">{docCustomText.length}/500</span>
                </div>

                {docError && <div className="dr-error-msg">{docError}</div>}

                {/* Submit */}
                <div className="dr-footer">
                  <button type="submit" className="dr-submit-btn" disabled={docSubmitting || !docSelectedUnit || (docRequestItems.length === 0 && !docCustomText.trim())}>
                    {docSubmitting ? (
                      <span className="dr-spin" />
                    ) : (
                      <>{language === 'de' ? 'Anfrage senden' : 'Submit Request'}</>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
          <style>{`
            .dr-fullscreen { position: fixed; inset: 0; z-index: 1100; background: #0d0d0d; display: flex; flex-direction: column; overflow: hidden; }
            .dr-header { display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; border-bottom: 1px solid rgba(214,177,96,0.15); flex-shrink: 0; }
            .dr-header h1 { margin: 0; font-size: 18px; font-weight: 600; color: #fff; letter-spacing: -0.3px; }
            .dr-back { background: none; border: none; color: #999; padding: 8px; cursor: pointer; border-radius: 10px; display: flex; align-items: center; justify-content: center; }
            .dr-back:active { background: rgba(255,255,255,0.05); }
            .dr-body { flex: 1; overflow-y: auto; padding: 24px 20px 120px; -webkit-overflow-scrolling: touch; }
            .dr-section { margin-bottom: 28px; }
            .dr-section-label { display: block; font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 1.2px; color: #D6B160; margin-bottom: 12px; }
            .dr-unit-dropdown { width: 100%; padding: 14px 16px; background: rgba(255,255,255,0.06); border: 1.5px solid rgba(214,177,96,0.3); border-radius: 14px; color: #fff; font-size: 15px; font-family: inherit; cursor: pointer; appearance: none; -webkit-appearance: none; background-image: url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23D6B160' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 16px center; }
            .dr-unit-dropdown:focus { outline: none; border-color: #D6B160; box-shadow: 0 0 0 3px rgba(214,177,96,0.15); }
            .dr-unit-dropdown option { background: #1a1a1a; color: #fff; padding: 10px; }
            .dr-no-units { padding: 20px; text-align: center; background: rgba(255,255,255,0.03); border-radius: 14px; border: 1px dashed rgba(255,255,255,0.1); }
            .dr-no-units p { margin: 0; color: #666; font-size: 14px; }
            .dr-doc-grid { display: flex; flex-wrap: wrap; gap: 8px; }
            .dr-doc-chip { display: inline-flex; align-items: center; gap: 6px; padding: 10px 16px; background: rgba(255,255,255,0.04); border: 1.5px solid rgba(255,255,255,0.1); border-radius: 24px; cursor: pointer; transition: all 0.2s; font-family: inherit; font-size: 13px; font-weight: 500; color: #aaa; white-space: nowrap; }
            .dr-doc-chip:active { transform: scale(0.96); }
            .dr-doc-active { border-color: #D6B160; background: rgba(214,177,96,0.12); color: #D6B160; }
            .dr-textarea { width: 100%; padding: 14px 16px; background: rgba(255,255,255,0.04); border: 1.5px solid rgba(255,255,255,0.08); border-radius: 14px; color: #fff; font-size: 15px; resize: none; box-sizing: border-box; font-family: inherit; line-height: 1.5; }
            .dr-textarea:focus { outline: none; border-color: rgba(214,177,96,0.4); }
            .dr-textarea::placeholder { color: #555; }
            .dr-char-count { display: block; text-align: right; font-size: 11px; color: #555; margin-top: 6px; }
            .dr-error-msg { background: rgba(239,68,68,0.1); border: 1px solid rgba(239,68,68,0.2); color: #ef4444; padding: 12px 16px; border-radius: 12px; margin-bottom: 16px; font-size: 14px; }
            .dr-footer { position: fixed; bottom: 0; left: 0; right: 0; padding: 20px 20px; padding-bottom: max(20px, env(safe-area-inset-bottom)); background: linear-gradient(to top, #0d0d0d 90%, transparent); z-index: 1101; }
            .dr-submit-btn { width: 100%; padding: 18px; background: linear-gradient(135deg, #D6B160 0%, #B8943D 100%); border: none; border-radius: 14px; color: #000; font-size: 17px; font-weight: 700; cursor: pointer; letter-spacing: 0.5px; display: flex; align-items: center; justify-content: center; gap: 8px; transition: all 0.2s; box-shadow: 0 4px 16px rgba(214,177,96,0.3); }
            .dr-submit-btn:active { transform: scale(0.98); opacity: 0.9; }
            .dr-submit-btn:disabled { opacity: 0.35; cursor: not-allowed; transform: none; }
            .dr-spin { width: 20px; height: 20px; border: 2.5px solid transparent; border-top-color: #000; border-radius: 50%; animation: drspin 0.7s linear infinite; }
            @keyframes drspin { to { transform: rotate(360deg); } }
            .dr-success-view { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 40px 20px; }
            .dr-success-icon { margin-bottom: 24px; }
            .dr-success-view h2 { color: #fff; font-size: 22px; font-weight: 600; margin: 0 0 8px; }
            .dr-success-view p { color: #888; font-size: 15px; margin: 0; text-align: center; line-height: 1.5; }
          `}</style>
        </>
      )}

      <div className="im-header-sticky">
        <div className="header-content">
          {/* Tapping the name opens the Menu rather than the profile page:
              the Menu's first card IS the profile, so nothing is lost, and it
              gives the menu a home on every phone width. The bottom bar has
              room for five tabs and no more. */}
          <button
            className="greeting-section greeting-menu-btn"
            onClick={() => setActiveTab('menu')}
            aria-label={t('menu')}
          >
            <span className="greeting-label">{getGreeting()},</span>
            <h1 className="investor-name">{displayName}</h1>
          </button>
          
          <div className="header-actions">
            <button className="action-btn whatsapp"
              onClick={() => {
                const msg = encodeURIComponent(`Hello Asset Desk, I'm ${fullName}, I need support for my Account`);
                window.open(`https://wa.me/971508418876?text=${msg}`, '_blank');
              }}
            >
              <svg viewBox="0 0 24 24" fill="currentColor" className="icon">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            </button>

            <button className="action-btn notification" onClick={() => setShowNotifications(!showNotifications)}>
              <Bell className="icon" />
              {unreadCount > 0 && <span className="badge">{unreadCount}</span>}
            </button>

            {/* ✅ NEW: Chat widget toggle button (replaces old openRespondIOChat) */}
            <button
              className="action-btn"
              onClick={() => onOpenChat?.()}
              aria-label={t('mdOpenChat')}
            >
              <MessageCircle className="icon" />
            </button>

            {/* The MANAGEMENT team, which is not the button to its left. That
                one opens the AI assistant; this one opens the written thread
                with the people who actually manage the property.
                
                It belongs here rather than only under More: an owner looking
                for "my messages" looks along this row, next to the bell —
                which is exactly where they were looking. Two chat icons side
                by side is not ideal, but one of them being unfindable is
                worse, and they do genuinely different things. */}
            <button
              className="action-btn notification"
              onClick={() => setActiveTab('messages')}
              aria-label={t('omTitle')}
              title={t('omSubtitle')}
            >
              <MessagesSquare className="icon" />
              {unreadMessages > 0 && <span className="badge">{unreadMessages > 9 ? '9+' : unreadMessages}</span>}
            </button>

            {/* This opens the Menu, so it shows a menu mark rather than the
                owner's initials — a button that says "SI" but does not go to
                the profile is a small lie. The profile is the first card
                inside the Menu. */}
            <button className="avatar-btn" onClick={() => setActiveTab('menu')} aria-label={t('menu')}>
              <div className="avatar avatar-menu">
                <MenuIcon size={19} strokeWidth={2.2} />
              </div>
            </button>
          </div>
        </div>
      </div>

      {showNotifications && (
        <>
          <div className="overlay" onClick={() => setShowNotifications(false)} />
          <div className="notifications-panel">
            <div className="panel-header">
              <h3>{t('notifications')}</h3>
              {unreadCount > 0 && <button onClick={markAllAsRead}>{t('markAllRead')}</button>}
            </div>
            <div className="notifications-list">
              {notifications.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px 16px', color: '#9ca3af' }}>
                  <Bell size={32} style={{ marginBottom: '8px', opacity: 0.3 }} />
                  <p style={{ margin: 0, fontSize: '14px' }}>
                    {language === 'de' ? 'Keine Benachrichtigungen' : 'No notifications yet'}
                  </p>
                </div>
              ) : notifications.map(n => (
                <div key={n.id} className={`notification-item ${!n.read ? 'unread' : ''}`}
                  onClick={() => {
                    if (n.actionUrl) {
                      if (n.actionType === 'external') { window.open(n.actionUrl, '_blank'); }
                      else { setActiveTab(n.actionUrl.replace('/', '')); }
                    }
                    setNotifications(prev => prev.map(notif => notif.id === n.id ? { ...notif, read: true } : notif));
                    setUnreadCount(prev => Math.max(0, prev - 1));
                  }}
                  style={{ cursor: n.actionUrl ? 'pointer' : 'default' }}
                >
                  <div className={`notif-icon ${n.type}`}>
                    {n.type === 'payment' && <CreditCard size={16} />}
                    {n.type === 'construction' && <Building2 size={16} />}
                    {n.type === 'document' && <FileText size={16} />}
                    {n.type === 'announcement' && <Bell size={16} />}
                    {n.type === 'webinar' && <Video size={16} />}
                    {n.type === 'journey' && <Plane size={16} />}
                    {n.type === 'event' && <Calendar size={16} />}
                    {n.type === 'reminder' && <Clock size={16} />}
                  </div>
                  <div className="notif-content">
                    <strong>{language === 'de' && n.titleDe ? n.titleDe : n.title}</strong>
                    <p>{language === 'de' && n.messageDe ? n.messageDe : n.message}</p>
                    {n.actionUrl && (
                      <span style={{ color: '#D6B160', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                        {n.actionType === 'external' ? <><Link size={12} /> {t('mdOpenLink')}</> : <><ArrowRight size={12} /> {t('mdView')}</>}
                      </span>
                    )}
                  </div>
                  <ChevronRight size={16} color="#ccc" />
                </div>
              ))}
            </div>
            {/* Push notification prompt — at bottom so it doesn't block notifications */}
            <InlineNotificationPrompt investorId={currentInvestor?.id || ''} apiUrl={apiUrl}
              onEnabled={() => {}} />
          </div>
        </>
      )}

      <div className="im-content">
        {/* Asset Desk KI speaks first: the daily briefing (Michael, 2026-10-15). */}
        <CopilotBriefing
          investorName={currentInvestor?.name}
          investorId={currentInvestor?.id}
          units={units as any[]}
          dldIntel={dldIntel}
          pmUnitsOverride={pmUnitsOverride}
          onAsk={(m) => (onAskCopilot ? onAskCopilot(m) : onOpenChat?.())}
          onOpenPortfolio={() => setActiveTab('portfolio')}
          onOpenWealth={onOpenWealth}
        />
        <div className="portfolio-card-mini">
          <div className="portfolio-row">
            <div className="portfolio-icon-mini"><Home size={14} color="#fff" /></div>
            <span className="portfolio-label-mini">{t('ipTotalInvested')}</span>
          </div>
          <h2 className="portfolio-value-mini">
            <span className="cur">AED</span>
            {totalInvested.toLocaleString('en-AE', { minimumFractionDigits: 0 })}
          </h2>
          {/* This figure is the sum of PURCHASE prices. It used to be captioned
              "valued from Dubai Land Department transactions", which it never
              was — the DLD valuation is the live estimate below it. */}
          {valuedPropertyCount > 0 && (
            <p className="portfolio-sub-mini">
              {t('ipInvestedSub').replace('{n}', String(valuedPropertyCount))}
            </p>
          )}

          {/* Estimated market value, counting up at the portfolio's own
              DLD-measured growth rate. Absent when the rate cannot be measured
              honestly (see dldValueGrowth) — never a made-up rate. */}
          {!growth && dldFetching && (
            <BrainLoader task="imbTaskValuation" compact dark />
          )}
          {growth && (() => {
            const locale = language === 'de' ? 'de-DE' : 'en-AE';
            const whole = (n: number) => Math.round(n).toLocaleString(locale);
            const gain = growth.valueToday - growth.invested;
            const pct = growth.invested > 0 ? (gain / growth.invested) * 100 : 0;
            const perDay = growth.valueToday * (Math.pow(1 + growth.rate, 1 / 365.25) - 1);
            return (
              <div className="pv-live">
                <span className="pv-live-label">
                  <span className="pv-live-dot" aria-hidden="true" /> {t('ipEstValueLive')}
                </span>
                <div className="pv-live-value">
                  <span className="cur">AED</span>
                  <LiveTicker at={valueAt} locale={locale} />
                </div>
                <p className="pv-live-sub">
                  {t('ipVsInvested')
                    .replace('{gain}', `${gain >= 0 ? '+' : '−'}AED ${whole(Math.abs(gain))}`)
                    .replace('{pct}', `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}`)}
                  {' · '}
                  {/* "growing" only when it is. A negative rate is measured on
                      fewer units than the gain beside it (LOW-confidence
                      valuations are left out of the rate), so it says so. */}
                  {(growth.rate >= 0 ? t('ipGrowingEst') : t('ipTrendEst'))
                    .replace('{n}', String(growth.basisUnits))
                    .replace('{rate}', `${growth.rate >= 0 ? '' : '−'}${Math.abs(growth.rate * 100).toFixed(1)}`)
                    .replace('{day}', `${perDay < 0 ? '−' : ''}${whole(Math.abs(perDay))}`)}
                </p>
                <p className="pv-live-src">
                  {t('ipValueSource').replace('{n}', String(growth.basisUnits))}
                </p>
                {/* The working, step by step, with this portfolio's own
                    numbers — so a figure that keeps moving can be checked. */}
                <details className="pv-calc">
                  <summary>{t('ipCalcTitle')}</summary>
                  <ol>
                    <li>
                      <span>{t('ipCalcValue').replace('{n}', String(growth.units))}</span>
                      <b>AED {whole(growth.valueToday)}</b>
                    </li>
                    <li>
                      <span>{t('ipCalcInvested')}</span>
                      <b>AED {whole(growth.invested)}</b>
                    </li>
                    <li>
                      <span>{t('ipCalcGain')}</span>
                      <b>{gain >= 0 ? '+' : '−'}AED {whole(Math.abs(gain))} ({pct >= 0 ? '+' : ''}{pct.toFixed(1)}%)</b>
                    </li>
                    <li className="pv-calc-sep">
                      <span>{t('ipCalcBasis').replace('{n}', String(growth.basisUnits))}</span>
                      <b>AED {whole(growth.basisCost)} → AED {whole(growth.basisValue)}</b>
                    </li>
                    <li>
                      <span>{t('ipCalcHeld')}</span>
                      <b>{growth.years.toFixed(2)} {t('ipCalcYears')}</b>
                    </li>
                    <li>
                      <span>
                        {t('ipCalcRate')}
                        <em>({whole(growth.basisValue)} ÷ {whole(growth.basisCost)})^(1 ÷ {growth.years.toFixed(2)}) − 1</em>
                      </span>
                      <b>{(growth.rate * 100).toFixed(2)}% p.a.</b>
                    </li>
                    <li>
                      <span>
                        {t('ipCalcDay')}
                        <em>{whole(growth.valueToday)} × ((1 + {(growth.rate * 100).toFixed(2)}%)^(1 ÷ 365) − 1)</em>
                      </span>
                      <b>AED {whole(perDay)}</b>
                    </li>
                  </ol>
                  <p className="pv-calc-note">
                    {t('ipCalcExcluded')
                      .replace('{low}', String(growth.excludedLow))
                      .replace('{nodate}', String(growth.excludedNoDate))}
                  </p>
                </details>
              </div>
            );
          })()}
          {/* The line under the figure, then the way in. Replaces the bare
              "View Details" link: this card is the first thing an owner sees,
              and a number with no shape to it says nothing about whether the
              portfolio is going anywhere. */}
          <PortfolioValueTrend
            units={units}
            totalValue={growth ? growth.valueToday : totalInvested}
            onOpen={() => setActiveTab('portfolio')}
          />
        </div>

        {/* The two questions a let portfolio raises: when does the next rent
            arrive, and what has the year made. Directly under the portfolio
            card; renders nothing when nothing is let. */}
        <PayoutSummary
          investorId={currentInvestor?.id}
          unitsOverride={pmUnitsOverride}
          onOpen={() => setActiveTab('property-management')}
        />

        {/* Reminders — the deadlines that cost money if they pass: a handover
            date, an instalment, the 90-day renewal notice. They were only ever
            behind the bell, which means an owner had to go looking for them.
            Unread, newest first, three at a time; "Show more" opens the rest in
            place. Identical copies of one reminder show as a single row. */}
        {(() => {
          const open = groupTodos([...paymentTodos, ...notifications].filter(n => !n.read && !n.done && !doneIds.includes(n.id)));
          if (open.length === 0) return null;
          const due = showAllTodos ? open : open.slice(0, 3);
          const hidden = open.length - 3;

          const tone = (type: string) =>
            type === 'payment' ? { bg: '#fef2f2', fg: '#b91c1c' }
            : type === 'construction' ? { bg: '#fffbeb', fg: '#b45309' }
            : type === 'reminder' ? { bg: '#eff6ff', fg: '#1d4ed8' }
            : { bg: '#f5f3ff', fg: '#6d28d9' };

          return (
            <div className="section">
              <div className="section-header">
                <h2>{language === 'de' ? 'Zu erledigen' : 'Needs your attention'}</h2>
                <button className="link-btn" onClick={() => setActiveTab('todos')}>
                  {language === 'de' ? 'Alle Aufgaben' : 'All to-dos'}
                </button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {due.map(({ n, ids, key }) => {
                  const c = tone(n.type);
                  return (
                    <div
                      key={key}
                      onClick={() => (n.id.startsWith('pay-') ? setActiveTab('payments') : setShowNotifications(true))}
                      style={{
                        display: 'flex', gap: '12px', alignItems: 'flex-start',
                        background: '#fff', borderRadius: '14px', padding: '14px',
                        border: '1px solid #e5e7eb', cursor: 'pointer',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                      }}
                    >
                      {/* Tick it off: done, so it leaves this list for good.
                          Its own tap target — the rest of the row still opens
                          the bell. */}
                      <button
                        type="button"
                        role="checkbox"
                        aria-checked={false}
                        aria-label={language === 'de' ? 'Als erledigt markieren' : 'Mark as done'}
                        title={language === 'de' ? 'Erledigt' : 'Done'}
                        className="todo-check"
                        onClick={(e) => { e.stopPropagation(); markDone(ids); }}
                        style={{
                          width: '26px', height: '26px', minWidth: '26px', marginTop: '6px',
                          borderRadius: '8px', border: '2px solid #d1d5db', background: '#fff',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          cursor: 'pointer', padding: 0, color: 'transparent',
                        }}
                      >
                        <Check size={16} strokeWidth={3} />
                      </button>
                      <div style={{
                        width: '38px', height: '38px', minWidth: '38px', borderRadius: '11px',
                        background: c.bg, color: c.fg,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {n.type === 'payment' ? <CreditCard size={18} />
                          : n.type === 'construction' ? <Building2 size={18} />
                          : n.type === 'reminder' ? <Clock size={18} />
                          : <FileText size={18} />}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '14px', fontWeight: 700, color: '#111827', lineHeight: 1.35 }}>
                          {language === 'de' && n.titleDe ? n.titleDe : n.title}
                        </div>
                        <div style={{
                          fontSize: '14px', color: '#6b7280', marginTop: '3px', lineHeight: 1.45,
                          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                        }}>
                          {language === 'de' && n.messageDe ? n.messageDe : n.message}
                        </div>
                      </div>
                      <ChevronRight size={18} color="#d1d5db" style={{ flexShrink: 0, marginTop: '9px' }} />
                    </div>
                  );
                })}
              </div>
              {hidden > 0 && (
                <button
                  type="button"
                  className="link-btn"
                  onClick={() => setShowAllTodos(v => !v)}
                  style={{ display: 'block', margin: '10px auto 0', fontWeight: 600 }}
                >
                  {showAllTodos
                    ? (language === 'de' ? 'Weniger anzeigen' : 'Show less')
                    : (language === 'de' ? `Mehr anzeigen (${hidden})` : `Show more (${hidden})`)}
                </button>
              )}
            </div>
          );
        })()}

        {/* Feedback & Ideas. It sits below the reminders rather than directly
            under the portfolio value, so the owner's money leads the screen —
            but it stays a full card, not a small tile, because it is how they
            tell us what to build next. */}
        <div
          onClick={() => setShowFeatureModal(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: '14px', width: '100%',
            padding: 'clamp(14px, 4.2vw, 18px) clamp(15px, 4.4vw, 20px)',
            marginBottom: '18px',
            background: 'linear-gradient(135deg, #1a1a2e, #16213e)',
            borderRadius: '16px', cursor: 'pointer',
            boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
            position: 'relative', overflow: 'hidden'
          }}
        >
          <div style={{
            position: 'absolute', top: '-15px', right: '-15px',
            width: '70px', height: '70px', borderRadius: '50%',
            background: 'rgba(214, 177, 96, 0.08)'
          }} />
          <div style={{
            width: 'clamp(44px, 12vw, 52px)', height: 'clamp(44px, 12vw, 52px)',
            borderRadius: '14px', flexShrink: 0,
            background: 'linear-gradient(135deg, #D6B160, #e8c97a)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(214, 177, 96, 0.3)'
          }}>
            <Lightbulb size={26} color="#fff" />
          </div>
          <div style={{ textAlign: 'left', flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontSize: 'clamp(15px, 4.2vw, 17px)', fontWeight: 700, color: '#ffffff' }}>
              {language === 'de' ? 'Feedback & Ideen' : 'Feedback & Ideas'}
            </p>
            <p style={{ margin: '4px 0 0', fontSize: 'clamp(11.5px, 3.2vw, 13px)', color: 'rgba(255,255,255,0.6)' }}>
              {language === 'de' ? 'Teilen Sie Ihre Ideen mit uns' : 'Share your ideas with us'}
            </p>
          </div>
          <div style={{
            width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0,
            background: 'rgba(214, 177, 96, 0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <span style={{ color: '#D6B160', fontSize: '20px', fontWeight: 700 }}>›</span>
          </div>
        </div>

        <div className="section properties-section">
          <div className="section-header">
            <h2>{t('myProperties')}</h2>
            <button className="link-btn" onClick={() => setActiveTab('properties')}>{t('viewAll')}</button>
          </div>
          {dashboardUnits.length > 0 ? (
            <>
              <PropertyCard unit={dashboardUnits[0]} />
              {dashboardUnits.length > 1 && (
                <button className="expand-btn" onClick={() => setExpandedProperties(!expandedProperties)}>
                  {t('myProperties')} ({String(propertyCount).padStart(2, '0')})
                  <ChevronDown className={`chevron ${expandedProperties ? 'expanded' : ''}`} size={16} />
                </button>
              )}
              {expandedProperties && dashboardUnits.slice(1).map(unit => <PropertyCard key={unit.id} unit={unit} />)}
            </>
          ) : (
            <div className="empty-state">
              <Building2 size={36} color="#D6B160" />
              <p>{t('noPropertiesYet')}</p>
              <button onClick={() => setActiveTab('IM Properties')}>{t('browseProperties')}</button>
            </div>
          )}
        </div>

        <div className="hero-banner" onClick={() => setActiveTab('IM Properties')}>
          <div className="hero-icon"><TrendingUp size={22} color="#D6B160" /></div>
          <h2 className="hero-title">{t('investInDubai')}</h2>
          <p className="hero-subtitle">{t('discoverExclusiveProperties')}</p>
          <button className="hero-cta">{t('exploreProperties')} <ArrowRight size={16} /></button>
          <div className="hero-decoration">
            <div className="deco-circle deco-1"></div>
            <div className="deco-circle deco-2"></div>
          </div>
        </div>

        <div className="section">
          <h2>{t('requests')}</h2>
          <div className="request-row">
            {/* Feedback has its own card above, so it is not repeated here. */}
            {requestActions.filter(a => a.id !== 'feedback').map(action => (
              <button key={action.id} className="request-item" onClick={action.action}>
                <div className="request-icon-circle"><span>{action.icon}</span></div>
                <span className="request-text">{action.label}</span>
              </button>
            ))}
          </div>

          {/* Upload Documents via Email */}
          <div
            onClick={() => {
              const email = 'documents@assetdesk.ae';
              const investorId = currentInvestor?.id || '';
              const investorEmail = currentInvestor?.email || '';
              const subject = encodeURIComponent(`Documents from ${currentInvestor?.name || (appLanguage() === 'de' ? 'Investor/in' : 'Investor')} [ID:${investorId}]`);
              const body = encodeURIComponent(`Dear Asset Desk Team,\n\nPlease find my documents attached.\n\n---\nInvestor: ${currentInvestor?.name || ''}\nID: ${investorId}\nEmail: ${investorEmail}\n(Please do not remove this information)`);
              const cc = investorEmail ? `&cc=${encodeURIComponent(investorEmail)}` : '';
              window.location.href = `mailto:${email}?subject=${subject}&body=${body}${cc}`;
            }}
            style={{
              marginTop: '16px',
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #1a1a2e, #16213e)',
              padding: '20px',
              cursor: 'pointer',
              boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <div style={{
              position: 'absolute', top: '-15px', right: '-15px',
              width: '70px', height: '70px', borderRadius: '50%',
              background: 'rgba(214, 177, 96, 0.08)'
            }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', position: 'relative' }}>
              <div style={{
                width: '48px', height: '48px', borderRadius: '14px', flexShrink: 0,
                background: 'linear-gradient(135deg, #D6B160, #e8c97a)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(214, 177, 96, 0.3)'
              }}>
                <Mail size={24} color="#fff" />
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ color: '#fff', fontSize: '16px', fontWeight: '700', margin: '0 0 4px' }}>
                  {language === 'de' ? 'Dokumente einreichen' : 'Submit Documents'}
                </p>
                <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14.5px', margin: 0 }}>
                  {language === 'de'
                    ? 'Per E-Mail senden - wir laden sie fuer Sie hoch'
                    : 'Email us your files - we upload them for you'}
                </p>
              </div>
              <div style={{
                width: '36px', height: '36px', borderRadius: '50%',
                background: 'rgba(214, 177, 96, 0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <span style={{ color: '#D6B160', fontSize: '18px' }}>›</span>
              </div>
            </div>
          </div>
        </div>

        {/* ===== SERVICE DETAIL MODAL ===== */}
        {selectedService && (() => {
          const service = services.find(s => s.id === selectedService);
          if (!service) return null;
          const docs = language === 'de' ? service.documentsDe : service.documents;
          return (
            <>
              <div style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
                zIndex: 1200, backdropFilter: 'blur(4px)'
              }} onClick={() => { setSelectedService(null); setConveyanceSuccess(false); setConveyanceError(null); }} />
              <div style={{
                position: 'fixed', bottom: '95px', left: '50%', transform: 'translateX(-50%)',
                width: '100%', maxWidth: '480px',
                background: '#fff', borderRadius: '24px 24px 0 0',
                zIndex: 1201, maxHeight: 'calc(100vh - 120px)', overflow: 'hidden',
                display: 'flex', flexDirection: 'column',
                animation: 'svcSlideUp 0.3s ease-out',
                boxShadow: '0 -4px 24px rgba(0,0,0,0.15)'
              }}>
                {/* Header with back button */}
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '14px 16px 8px', flexShrink: 0
                }}>
                  <button onClick={() => { setSelectedService(null); setConveyanceSuccess(false); setConveyanceError(null); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '4px', color: '#6b7280', fontSize: '14px', fontWeight: 500 }}>
                    <ChevronLeft size={20} /> {language === 'de' ? 'Zurück' : 'Back'}
                  </button>
                  <div style={{ width: '32px', height: '4px', borderRadius: '2px', background: '#d1d5db' }} />
                  <div style={{ width: '60px' }} />
                </div>

                {/* Scrollable content */}
                <div style={{ overflowY: 'auto', padding: '0 20px 0', flex: 1 }}>
                  {/* Service header */}
                  <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                    <div style={{
                      width: '56px', height: '56px', borderRadius: '18px',
                      background: 'linear-gradient(135deg, #f8f6f1, #f0ebe0)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '28px', margin: '0 auto 10px'
                    }}>
                      {service.icon}
                    </div>
                    <h2 style={{ margin: '0 0 6px', fontSize: '18px', fontWeight: 700, color: '#111827' }}>
                      {language === 'de' ? service.titleDe : service.title}
                    </h2>
                    <p style={{ margin: 0, fontSize: '14.5px', color: '#6b7280', lineHeight: '1.4' }}>
                      {language === 'de' ? service.descriptionDe : service.description}
                    </p>
                  </div>

                  {/* Documents needed */}
                  <div style={{
                    background: '#f9fafb', borderRadius: '14px', padding: '14px',
                    border: '1px solid #f3f4f6', marginBottom: '16px'
                  }}>
                    <h3 style={{ margin: '0 0 10px', fontSize: '14.5px', fontWeight: 700, color: '#111827',
                      display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <FileText size={14} color="#D6B160" />
                      {language === 'de' ? 'Benötigte Dokumente' : 'Documents Required'}
                    </h3>
                    <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                      {docs.map((doc, idx) => (
                        <li key={idx} style={{
                          fontSize: '14px', color: '#374151', padding: '5px 0',
                          borderBottom: idx < docs.length - 1 ? '1px solid #f3f4f6' : 'none',
                          display: 'flex', alignItems: 'flex-start', gap: '6px'
                        }}>
                          <CheckCircle size={12} color="#D6B160" style={{ flexShrink: 0, marginTop: '2px' }} />
                          {doc}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Note */}
                  {service.note && (
                    <p style={{ fontSize: '13.5px', color: '#6b7280', fontStyle: 'italic', margin: '0 0 16px', lineHeight: '1.4' }}>
                      {language === 'de' ? service.noteDe : service.note}
                    </p>
                  )}

                  {conveyanceError && (
                    <p style={{ color: '#dc2626', fontSize: '14px', margin: '0 0 12px', background: '#fef2f2', padding: '8px 10px', borderRadius: '8px' }}>
                      {conveyanceError}
                    </p>
                  )}

                  {/* Success message */}
                  {conveyanceSuccess && (
                    <div style={{
                      textAlign: 'center', padding: '20px', background: '#f0fdf4',
                      borderRadius: '14px', border: '1px solid #bbf7d0', marginBottom: '16px'
                    }}>
                      <CheckCircle size={40} color="#22c55e" />
                      <h3 style={{ margin: '10px 0 6px', fontSize: '16px', fontWeight: 700, color: '#111827' }}>
                        {language === 'de' ? 'Anfrage gesendet!' : 'Enquiry Submitted!'}
                      </h3>
                      <p style={{ margin: 0, fontSize: '14.5px', color: '#6b7280', lineHeight: '1.4' }}>
                        {language === 'de'
                          ? 'Unser Team wird sich umgehend bei Ihnen melden.'
                          : 'Our team will contact you immediately.'}
                      </p>
                    </div>
                  )}
                </div>

                {/* Fixed CTA button at bottom */}
                {!conveyanceSuccess && (
                  <div style={{
                    padding: '12px 20px', paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
                    borderTop: '1px solid #f3f4f6', background: '#fff', flexShrink: 0
                  }}>
                    {service.type === 'corporate' && (
                      <button
                        onClick={() => {
                          if ((window as any).Calendly) {
                            (window as any).Calendly.initPopupWidget({
                              url: 'https://calendly.com/j-kijasko-investmenter/internationale-steuerberatung-investmenter-app',
                              prefill: {
                                name: currentInvestor?.name || '',
                                email: currentInvestor?.email || '',
                              },
                            });
                          } else {
                            // Fallback: open in new tab if widget not loaded
                            window.open('https://calendly.com/j-kijasko-investmenter/internationale-steuerberatung-investmenter-app', '_blank');
                          }
                        }}
                        style={{
                          width: '100%', padding: '15px', borderRadius: '14px',
                          background: 'linear-gradient(135deg, #D6B160 0%, #B8943D 100%)',
                          border: 'none', color: '#fff', fontSize: '15px', fontWeight: 700,
                          cursor: 'pointer', display: 'flex', alignItems: 'center',
                          justifyContent: 'center', gap: '8px',
                          boxShadow: '0 4px 16px rgba(214,177,96,0.3)'
                        }}
                      >
                        <Calendar size={16} />
                        {language === 'de' ? 'Beratungstermin buchen' : 'Book a Consultation'}
                      </button>
                    )}

                    {service.type === 'conveyance' && (
                      <button
                        onClick={() => handleServiceEnquiry(language === 'de' ? service.titleDe : service.title)}
                        disabled={conveyanceSubmitting}
                        style={{
                          width: '100%', padding: '15px', borderRadius: '14px',
                          background: conveyanceSubmitting ? '#d1d5db' : 'linear-gradient(135deg, #D6B160 0%, #B8943D 100%)',
                          border: 'none', color: '#fff', fontSize: '15px', fontWeight: 700,
                          cursor: conveyanceSubmitting ? 'wait' : 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                          boxShadow: '0 4px 16px rgba(214,177,96,0.3)'
                        }}
                      >
                        {conveyanceSubmitting
                          ? (language === 'de' ? 'Wird gesendet...' : 'Submitting...')
                          : (<><Phone size={16} /> {language === 'de' ? 'Anfrage senden' : 'Enquire Now'}</>)}
                      </button>
                    )}
                  </div>
                )}
              </div>
              <style>{`
                @keyframes svcSlideUp {
                  from { transform: translateY(100%); }
                  to { transform: translateY(0); }
                }
              `}</style>
            </>
          );
        })()}

        <div className="section">
          <div className="section-header">
            <h2>{language === 'de' ? 'Dubai Nachrichten' : 'Dubai News'}</h2>
          </div>
          <div className="news-list">
            {newsItems.map(news => (
              <div key={news.id} className="news-item" onClick={() => news.url && window.open(news.url, '_blank')}
                style={{ cursor: news.url ? 'pointer' : 'default' }}>
                <div className="news-item-content">
                  {/* German headline and summary are translated on the server
                      (titleDe / summaryDe); English shows until they exist. */}
                  <h4>{language === 'de' && (news as any).titleDe ? (news as any).titleDe : news.title}</h4>
                  <p>{language === 'de' && (news as any).titleDe ? ((news as any).summaryDe ?? news.summary) : news.summary}</p>
                </div>
                <span className="news-date">{newsAge((news as any).publishedAt, news.date, language === 'de')}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="section">
          <div className="section-header">
            <h2>{t('upcomingProjects')}</h2>
            <button className="link-btn" onClick={() => setActiveTab('IM Properties')}>{t('viewAll')}</button>
          </div>
          <p className="section-subtitle">{t('exclusiveLaunches')}</p>
          <div className="projects-carousel">
            {upcomingProjects.map(project => (
              <div key={project.id} className="project-card">
                <div className="project-media">
                  {project.video ? (
                    <VideoPlayer videoUrl={project.video} projectName={project.name} />
                  ) : project.images && project.images.length > 0 ? (
                    <ImageSlideshow images={project.images} projectName={project.name} />
                  ) : (
                    <div className="project-placeholder"><Building2 size={40} color="#D6B160" /></div>
                  )}
                  <span className="launch-badge">{project.launchDate}</span>
                </div>
                <div className="project-info">
                  <h3>{project.name}</h3>
                  <p className="project-developer">by {project.developer}</p>
                  <p className="project-location"><MapPin size={12} /> {project.location}</p>
                  {project.brochureUrl && (
                    <button className="brochure-btn" onClick={(e) => { e.stopPropagation(); window.open(project.brochureUrl, '_blank'); }}>
                      <ExternalLink size={14} /> {t('viewBrochure')}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* New Listings from Sell Platform */}
        {(newListings.length > 0 || listingsLoading) && (
          <div className="section">
            <div className="section-header">
              <h2>{language === 'de' ? 'Neue Angebote' : 'New Listings'}</h2>
            </div>
            <p className="section-subtitle">
              {language === 'de' ? 'Aktuelle Immobilien auf unserer Verkaufsplattform' : 'Latest properties on our sell platform'}
            </p>

            {listingsLoading ? (
              <div style={{ textAlign: 'center', padding: '32px 0', color: '#9ca3af' }}>
                <div style={{
                  width: '32px', height: '32px', border: '3px solid #e5e7eb',
                  borderTopColor: '#D6B160', borderRadius: '50%',
                  animation: 'spin 1s linear infinite', margin: '0 auto 12px'
                }} />
                <p style={{ fontSize: '14.5px', margin: 0 }}>{language === 'de' ? 'Laden...' : 'Loading listings...'}</p>
              </div>
            ) : (
              <div style={{
                display: 'flex', gap: '16px', overflowX: 'auto',
                paddingBottom: '12px', scrollSnapType: 'x mandatory',
                WebkitOverflowScrolling: 'touch'
              }}>
                {newListings.map(listing => (
                  <div key={listing.id} style={{
                    minWidth: '300px', maxWidth: '320px', scrollSnapAlign: 'start',
                    borderRadius: '16px', overflow: 'hidden',
                    background: '#fff', border: '1px solid #e5e7eb',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                    flexShrink: 0, display: 'flex', flexDirection: 'column'
                  }}>
                    {/* Property Image — from documents table or fallback */}
                    <div style={{ position: 'relative', height: '180px', overflow: 'hidden' }}>
                      <img
                        src={listing.photoUrl || propertyTypeImages[listing.propertyType] || propertyTypeImages.apartment}
                        alt={listing.title || listing.propertyType}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={(e) => { (e.target as HTMLImageElement).src = propertyTypeImages[listing.propertyType] || propertyTypeImages.apartment; }}
                      />
                      {listing.isDistress && (
                        <div style={{
                          position: 'absolute', top: '10px', right: '10px',
                          background: '#dc2626', color: '#fff', padding: '4px 10px',
                          borderRadius: '8px', fontSize: '13.5px', fontWeight: 700,
                          display: 'flex', alignItems: 'center', gap: '4px'
                        }}>
                          <Flame size={14} /> {t('mdDistressSale')}
                        </div>
                      )}
                    </div>

                    {/* Badges row */}
                    <div style={{ padding: '12px 16px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{
                        padding: '4px 12px', borderRadius: '20px', fontSize: '14px', fontWeight: 600,
                        background: '#f3f4f6', color: '#374151', textTransform: 'capitalize', border: '1px solid #e5e7eb'
                      }}>
                        {listing.propertyType}
                      </span>
                      {listing.urgency && (
                        <span style={{
                          padding: '4px 10px', borderRadius: '20px', fontSize: '13.5px', fontWeight: 600,
                          background: listing.urgency === '3days' ? '#fef2f2' : '#f0f9ff',
                          color: listing.urgency === '3days' ? '#dc2626' : '#6b7280',
                          border: `1px solid ${listing.urgency === '3days' ? '#fecaca' : '#e5e7eb'}`,
                          display: 'flex', alignItems: 'center', gap: '4px'
                        }}>
                          <Timer size={12} /> {urgencyLabel(listing.urgency)}
                        </span>
                      )}
                    </div>

                    {/* Title */}
                    <div style={{ padding: '8px 16px 0' }}>
                      <p style={{ margin: 0, fontWeight: 600, fontSize: '15px', color: '#111827' }}>
                        {listing.title || `${listing.location} – ${trData(listing.propertyType.charAt(0).toUpperCase() + listing.propertyType.slice(1))}`}
                      </p>
                    </div>

                    {/* Price — asking + market value + discount % */}
                    <div style={{ padding: '6px 16px 0' }}>
                      {listing.askingPrice && (
                        <p style={{ margin: 0, fontWeight: 800, fontSize: '20px', color: '#111827' }}>
                          AED {listing.askingPrice.toLocaleString('en-AE')}
                        </p>
                      )}
                      {listing.isDistress && listing.marketValue && listing.marketValue > 0 && (
                        <p style={{
                          margin: '2px 0 0', fontSize: '14.5px', color: '#166534', fontWeight: 600
                        }}>
                          {appLanguage() === 'de' ? 'Marktwert' : 'Market Value'}: AED {listing.marketValue.toLocaleString('en-AE')}
                        </p>
                      )}
                      {listing.isDistress && listing.discountPercent && (
                        <p style={{ margin: '2px 0 0', fontSize: '14.5px', fontWeight: 700, color: '#dc2626' }}>
                          ↘ {listing.discountPercent}% {appLanguage() === 'de' ? 'unter Marktwert' : 'below market value'}
                        </p>
                      )}
                    </div>

                    {/* Details: location, bedrooms, size */}
                    <div style={{ padding: '10px 16px 0', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <MapPin size={14} color="#9ca3af" />
                        <span style={{ fontSize: '14.5px', color: '#6b7280' }}>{listing.location}</span>
                      </div>
                      {listing.bedrooms && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <BedDouble size={14} color="#9ca3af" />
                          <span style={{ fontSize: '14.5px', color: '#6b7280' }}>
                            {listing.bedrooms} {appLanguage() === 'de' ? (listing.bedrooms === 1 ? 'Schlafzimmer' : 'Schlafzimmer') : (listing.bedrooms === 1 ? 'Bedroom' : 'Bedrooms')}
                          </span>
                        </div>
                      )}
                      {listing.sizeSqft && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Ruler size={14} color="#9ca3af" />
                          <span style={{ fontSize: '14.5px', color: '#6b7280' }}>{listing.sizeSqft.toLocaleString()} sqft</span>
                        </div>
                      )}
                    </div>

                    {/* Express Interest Button */}
                    <div style={{ padding: '14px 16px', marginTop: 'auto' }}>
                      {enquiryDone[listing.id] ? (
                        <div style={{
                          width: '100%', padding: '12px', borderRadius: '10px',
                          background: '#f0fdf4', border: '1px solid #bbf7d0',
                          textAlign: 'center', fontSize: '14px', fontWeight: 600, color: '#16a34a',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                        }}>
                          <CheckCircle size={14} /> {language === 'de' ? 'Interesse gesendet' : 'Interest Sent'}
                        </div>
                      ) : (
                        <button
                          onClick={() => setEnquiryListing(listing)}
                          style={{
                            width: '100%', padding: '12px', borderRadius: '10px',
                            background: 'linear-gradient(135deg, #D6B160, #c9a14e)',
                            border: 'none', cursor: 'pointer',
                            fontSize: '14px', fontWeight: 700, color: '#fff',
                            boxShadow: '0 2px 8px rgba(214, 177, 96, 0.3)',
                            transition: 'transform 0.15s, box-shadow 0.15s'
                          }}
                          onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(214, 177, 96, 0.4)'; }}
                          onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(214, 177, 96, 0.3)'; }}
                        >
                          {language === 'de' ? 'Interesse bekunden' : 'Express Interest'}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ===== AVAILABLE TO YOU =====
            Replaces the two catalogue carousels (Corporate / Conveyance),
            which showed every service to everybody. This shows only what the
            investor's own portfolio qualifies for, one tap each:
              - Golden Visa: holds a Dubai property -> WhatsApp the visa line
              - Company formation: held portfolio >= AED 2m -> Julia's Calendly
              - Tax consultation: holds a Dubai property -> Julia's Calendly
            The portfolio figure is the same one the hero card shows (DLD value
            today, else what was paid), so the 2m line never disagrees with it. */}
        {eligibleServices.length > 0 && (
          <div className="section">
            <div className="section-header">
              <h2>{language === 'de' ? 'Für Sie verfügbar' : 'Available to you'}</h2>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
              {eligibleServices.map(s => (
                <button
                  key={s.id}
                  onClick={s.onOpen}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '14px', textAlign: 'left',
                    padding: '16px', borderRadius: '16px', cursor: 'pointer',
                    background: '#000', border: '1px solid rgba(214,177,96,0.35)',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.10)', width: '100%',
                  }}
                >
                  <span style={{
                    width: '44px', height: '44px', borderRadius: '12px', flexShrink: 0,
                    background: '#D6B160', color: '#000',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {s.icon}
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', color: '#fff', fontSize: '15px', fontWeight: 700, lineHeight: 1.25 }}>
                      {s.title}
                    </span>
                    <span style={{ display: 'block', color: 'rgba(255,255,255,0.62)', fontSize: '13px', marginTop: '3px', lineHeight: 1.35 }}>
                      {s.reason}
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#D6B160', fontSize: '13.5px', fontWeight: 700, marginTop: '8px' }}>
                      {s.cta}
                    </span>
                  </span>
                  <ChevronRight size={18} color="#D6B160" style={{ flexShrink: 0 }} />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Enquiry Confirmation Modal */}
        {enquiryListing && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
          }} onClick={() => { if (!enquirySubmitting) setEnquiryListing(null); }}>
            <div style={{
              background: '#fff', borderRadius: '20px', maxWidth: '400px', width: '100%',
              padding: '28px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
            }} onClick={(e) => e.stopPropagation()}>
              <h3 style={{ margin: '0 0 4px', fontSize: '18px', fontWeight: 700, color: '#111827' }}>
                {language === 'de' ? 'Interesse bestätigen' : 'Confirm Interest'}
              </h3>
              <p style={{ margin: '0 0 20px', fontSize: '14.5px', color: '#6b7280' }}>
                {language === 'de'
                  ? 'Ihre Kontaktdaten werden an unser Team gesendet.'
                  : 'Your contact details will be sent to our team.'}
              </p>

              {/* Property summary */}
              <div style={{
                padding: '14px', borderRadius: '12px', background: '#f9fafb',
                border: '1px solid #f3f4f6', marginBottom: '16px'
              }}>
                <p style={{ margin: '0 0 4px', fontWeight: 600, fontSize: '14px', color: '#111827', textTransform: 'capitalize' }}>
                  {enquiryListing.propertyType} in {enquiryListing.location}
                </p>
                {enquiryListing.askingPrice && (
                  <p style={{ margin: 0, fontWeight: 700, fontSize: '16px', color: '#D6B160' }}>
                    AED {enquiryListing.askingPrice.toLocaleString('en-AE')}
                  </p>
                )}
              </div>

              {/* Investor info */}
              <div style={{
                padding: '14px', borderRadius: '12px', background: '#f0f9ff',
                border: '1px solid #bae6fd', marginBottom: '20px'
              }}>
                <p style={{ margin: '0 0 6px', fontSize: '14px', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {language === 'de' ? 'Ihre Daten' : 'Your Details'}
                </p>
                <p style={{ margin: '0 0 2px', fontSize: '14px', fontWeight: 600, color: '#111827' }}>
                  {currentInvestor?.name || (appLanguage() === 'de' ? 'Investor/in' : 'Investor')}
                </p>
                <p style={{ margin: 0, fontSize: '14.5px', color: '#6b7280' }}>
                  {currentInvestor?.email || ''}
                </p>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => setEnquiryListing(null)}
                  disabled={enquirySubmitting}
                  style={{
                    flex: 1, padding: '12px', borderRadius: '10px',
                    background: '#f3f4f6', border: '1px solid #e5e7eb',
                    fontSize: '14px', fontWeight: 600, color: '#374151', cursor: 'pointer'
                  }}
                >
                  {language === 'de' ? 'Abbrechen' : 'Cancel'}
                </button>
                <button
                  onClick={() => handleEnquiry(enquiryListing)}
                  disabled={enquirySubmitting}
                  style={{
                    flex: 2, padding: '12px', borderRadius: '10px',
                    background: 'linear-gradient(135deg, #D6B160, #c9a14e)',
                    border: 'none', fontSize: '14px', fontWeight: 700, color: '#fff',
                    cursor: enquirySubmitting ? 'wait' : 'pointer',
                    opacity: enquirySubmitting ? 0.7 : 1
                  }}
                >
                  {enquirySubmitting
                    ? (language === 'de' ? 'Senden...' : 'Sending...')
                    : (language === 'de' ? 'Interesse senden' : 'Send Interest')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Contextual Quick Feedback Toast */}
      {showContextualFeedback && (
        <div style={{
          position: 'fixed', bottom: 100, left: '50%', transform: 'translateX(-50%)',
          width: '90%', maxWidth: 400, background: '#1a1a2e', borderRadius: 14,
          padding: '16px 18px', zIndex: 999, boxShadow: '0 8px 30px rgba(0,0,0,0.35)',
          border: '1px solid rgba(214,177,96,0.25)', animation: 'ctxFeedbackSlide 0.3s ease-out'
        }}>
          <button onClick={() => { setShowContextualFeedback(false); localStorage.setItem('ctxFeedbackDismissed_' + contextualFeedbackType, Date.now().toString()); }}
            style={{ position: 'absolute', top: 8, right: 8, background: 'none', border: 'none', color: '#666', cursor: 'pointer', padding: 4 }}>
            <X size={14} />
          </button>
          <p style={{ margin: '0 0 10px', color: '#fff', fontSize: 14, fontWeight: 600 }}>
            {contextualFeedbackType === 'portfolio'
              ? (language === 'de' ? 'Wie finden Sie Ihre Portfolio-Übersicht?' : 'How do you find your portfolio overview?')
              : contextualFeedbackType === 'document'
              ? (language === 'de' ? 'War der Dokumenten-Upload einfach?' : 'Was the document upload easy?')
              : (language === 'de' ? 'Wie gefällt Ihnen die App?' : 'How do you like the app?')}
          </p>
          <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 10 }}>
            {[1, 2, 3, 4, 5].map((star) => (
              <button key={star} onClick={async () => {
                await handleQuickRating(star, contextualFeedbackType || 'general');
                setShowContextualFeedback(false);
                localStorage.setItem('ctxFeedbackDismissed_' + contextualFeedbackType, Date.now().toString());
              }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}>
                <Star size={30} fill={star <= 3 ? 'transparent' : 'transparent'} color="#D6B160" strokeWidth={1.5} />
              </button>
            ))}
          </div>
          <button onClick={() => { setShowContextualFeedback(false); openFeedbackWithContext(contextualFeedbackType || 'general', contextualFeedbackType || 'general'); }}
            style={{ width: '100%', background: 'rgba(214,177,96,0.15)', border: '1px solid rgba(214,177,96,0.3)', borderRadius: 8, padding: '10px', color: '#D6B160', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
            {language === 'de' ? 'Ausführliches Feedback geben' : 'Give detailed feedback'}
          </button>
          <style>{`
            @keyframes ctxFeedbackSlide {
              from { opacity: 0; transform: translateX(-50%) translateY(20px); }
              to { opacity: 1; transform: translateX(-50%) translateY(0); }
            }
          `}</style>
        </div>
      )}

    </div>
  );
};

export default MainDashboard;